import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  TextField,
  Chip,
  Box,
  Divider,
  Tooltip,
  Typography,
  Stack,
  MenuItem,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import { activityApi } from '../../services/activityApi';

// --- Display-safe terminology helpers ---
// User-facing protected terminology used by larger platforms is intentionally avoided.
// Safer product terminology: Core Practice Area.
const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = ['Entrustable', 'Professional', 'Activities'].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function sanitizeStringArray(values) {
  if (!Array.isArray(values)) return [];
  return values.map((value) => sanitizeDisplayText(value)).filter(Boolean);
}

const PARTICIPATION_MODES = [
  'observed',
  'assisted',
  'performed_supervised',
  'performed_independent',
];

function normalizeParticipationMode(value) {
  const s = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const aliases = {
    observe: 'observed',
    observer: 'observed',
    observed: 'observed',
    assist: 'assisted',
    assisted: 'assisted',
    participated: 'assisted',
    participant: 'assisted',
    performed: 'performed_supervised',
    supervised: 'performed_supervised',
    performed_supervised: 'performed_supervised',
    performed_under_supervision: 'performed_supervised',
    independent: 'performed_independent',
    performed_independent: 'performed_independent',
    independent_assigned_task: 'performed_independent',
  };
  const mapped = aliases[s] || s;
  return PARTICIPATION_MODES.includes(mapped) ? mapped : '';
}

function participationModeLabel(value) {
  const mode = normalizeParticipationMode(value);
  if (mode === 'observed') return 'Observed';
  if (mode === 'assisted') return 'Assisted / Participated';
  if (mode === 'performed_supervised') return 'Performed / Led under supervision';
  if (mode === 'performed_independent') return 'Independent assigned task';
  return '';
}

function templateParticipationModes(template = {}) {
  return (Array.isArray(template?.participationModes) ? template.participationModes : [])
    .map((item) => ({
      ...(item || {}),
      key: normalizeParticipationMode(item?.key || item?.mode || item),
      label: sanitizeDisplayText(item?.label || participationModeLabel(item?.key || item)),
      description: sanitizeDisplayText(item?.description || ''),
    }))
    .filter((item) => item.key && item.allowed !== false);
}

function templateParticipationComponentOptions(template = {}, modeValue = '') {
  const mode = normalizeParticipationMode(modeValue);
  return (Array.isArray(template?.participationComponentOptions)
    ? template.participationComponentOptions
    : [])
    .map((item) => {
      const source = item && typeof item === 'object'
        ? item
        : { value: item, label: item };
      const value = sanitizeDisplayText(
        source.value || source.key || source.label || ''
      );
      const allowedForModes = Array.isArray(source.allowedForModes)
        ? source.allowedForModes.map(normalizeParticipationMode).filter(Boolean)
        : [];
      return {
        ...source,
        value,
        label: sanitizeDisplayText(source.label || value),
        description: sanitizeDisplayText(source.description || ''),
        allowedForModes,
      };
    })
    .filter((item) => {
      if (!item.value) return false;
      if (!mode || !item.allowedForModes.length) return true;
      return item.allowedForModes.includes(mode);
    });
}

function participationComponentsRequiredForMode(template = {}, modeValue = '') {
  const mode = normalizeParticipationMode(modeValue);
  if (!mode) return false;
  const requiredModes = Array.isArray(template?.participationComponentsRequiredForModes)
    ? template.participationComponentsRequiredForModes
        .map(normalizeParticipationMode)
        .filter(Boolean)
    : [];
  return requiredModes.includes(mode);
}

function normalizeParticipationComponents(values = []) {
  const input = Array.isArray(values) ? values : values ? [values] : [];
  return [...new Set(
    input
      .map((value) => sanitizeDisplayText(value))
      .filter(Boolean)
  )];
}

function isRoleAwareTemplate(template = {}) {
  return templateParticipationModes(template).length > 0;
}

function isStepVisibleForMode(step = {}, modeValue = '') {
  const mode = normalizeParticipationMode(modeValue);
  const visible = Array.isArray(step?.visibleForModes)
    ? step.visibleForModes.map(normalizeParticipationMode).filter(Boolean)
    : [];
  if (!mode || !visible.length) return true;
  return visible.includes(mode);
}

function isStepRequiredForMode(step = {}, modeValue = '') {
  const mode = normalizeParticipationMode(modeValue);
  const required = Array.isArray(step?.requiredForModes)
    ? step.requiredForModes.map(normalizeParticipationMode).filter(Boolean)
    : [];

  // For a role-aware run, the per-mode array is authoritative even when it is
  // empty. Empty means optional for that participation mode; falling back to the
  // legacy `required` flag would defeat the role-specific evidence design.
  if (mode && Array.isArray(step?.requiredForModes)) return required.includes(mode);
  return step?.required === true;
}

function pickFirstNonEmpty(...values) {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const s = String(value).trim();
    if (s) return s;
  }
  return '';
}

function personDisplayName(value) {
  if (value === undefined || value === null) return '';

  if (typeof value === 'object' && !Array.isArray(value)) {
    const candidates = [
      value.name,
      value.fullName,
      value.displayName,
      value.email,
      value.username,
      value.user?.name,
      value.user?.fullName,
      value.user?.displayName,
      value.user?.email,
      value.profile?.name,
      value.profile?.fullName,
    ];
    for (const candidate of candidates) {
      const hit = personDisplayName(candidate);
      if (hit) return hit;
    }
    return '';
  }

  const text = sanitizeDisplayText(value);
  if (!text || text === '[object Object]' || /^[a-f0-9]{24}$/i.test(text)) return '';
  return text;
}

function firstPersonDisplayName(...values) {
  for (const value of values) {
    const hit = personDisplayName(value);
    if (hit) return hit;
  }
  return '';
}

// --- Tenant-aware helpers to preserve /t/:slug across navigations ---
function safeJsonParse(v) {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}

function readTenantSlugFromStorage() {
  const candidates = [];
  try {
    const t1 = localStorage.getItem('tenantSlug');
    const t2 = localStorage.getItem('activeTenantSlug');
    const t3 = sessionStorage.getItem('tenantSlug');
    const t4 = sessionStorage.getItem('activeTenantSlug');
    if (t1) candidates.push(t1);
    if (t2) candidates.push(t2);
    if (t3) candidates.push(t3);
    if (t4) candidates.push(t4);

    const lu = safeJsonParse(localStorage.getItem('loggedInUser') || 'null');
    const su = safeJsonParse(sessionStorage.getItem('loggedInUser') || 'null');

    [lu, su].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(
        core?.tenantSlug,
        obj?.tenantSlug,
        core?.tenant?.slug,
        obj?.tenant?.slug
      );
    });
  } catch {
    // ignore
  }

  const slug = candidates
    .map((s) => String(s || '').trim())
    .find((s) => !!s);

  return slug || '';
}


function readUserRoleFromStorage() {
  try {
    const candidates = [];
    const lu = safeJsonParse(localStorage.getItem('loggedInUser') || 'null');
    const su = safeJsonParse(sessionStorage.getItem('loggedInUser') || 'null');
    [lu, su].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(core?.role, obj?.role);
    });

    const hit = candidates
      .map((x) => String(x || '').trim())
      .find((x) => !!x);

    return hit || '';
  } catch {
    return '';
  }
}

function readLoggedInUserIdentityFromStorage() {
  try {
    const storedUsers = [
      safeJsonParse(localStorage.getItem('loggedInUser') || 'null'),
      safeJsonParse(sessionStorage.getItem('loggedInUser') || 'null'),
      safeJsonParse(localStorage.getItem('user') || 'null'),
      safeJsonParse(sessionStorage.getItem('user') || 'null'),
      safeJsonParse(localStorage.getItem('currentUser') || 'null'),
      safeJsonParse(sessionStorage.getItem('currentUser') || 'null'),
    ].filter(Boolean);

    for (const stored of storedUsers) {
      const core = stored?.user || stored;
      const name = firstPersonDisplayName(
        core?.name,
        core?.fullName,
        core?.displayName,
        stored?.name,
        stored?.fullName,
        stored?.displayName
      );
      const rollNo = sanitizeDisplayText(
        pickFirstNonEmpty(core?.rollNo, stored?.rollNo, core?.registrationNumber, stored?.registrationNumber)
      );
      const role = sanitizeDisplayText(pickFirstNonEmpty(core?.role, stored?.role));
      const userId = pickFirstNonEmpty(core?._id, core?.id, stored?._id, stored?.id, stored?.userId);

      if (name || rollNo || role || userId) {
        return { name, rollNo, role, userId: String(userId || '') };
      }
    }
  } catch {
    // ignore storage failures
  }

  return { name: '', rollNo: '', role: '', userId: '' };
}

function activityOwnerIdentity(item = {}, fallback = {}) {
  const nestedUser =
    item?.internUser && typeof item.internUser === 'object'
      ? item.internUser
      : (item?.internUserId && typeof item.internUserId === 'object' ? item.internUserId : {});

  const docUserId = pickFirstNonEmpty(
    nestedUser?._id,
    nestedUser?.id,
    typeof item?.internUserId === 'object' ? '' : item?.internUserId,
    item?.userId
  );
  const fallbackUserId = pickFirstNonEmpty(fallback?.userId);
  const allowFallback =
    !docUserId ||
    !fallbackUserId ||
    String(docUserId) === String(fallbackUserId);

  const name = firstPersonDisplayName(
    item?.internName,
    item?.userName,
    item?.createdByName,
    item?.intern?.name,
    nestedUser,
    allowFallback ? fallback?.name : ''
  );

  const rollNo = sanitizeDisplayText(
    pickFirstNonEmpty(
      item?.internRollNo,
      item?.rollNo,
      item?.intern?.rollNo,
      nestedUser?.rollNo,
      allowFallback ? fallback?.rollNo : ''
    )
  );

  const role = sanitizeDisplayText(
    pickFirstNonEmpty(
      item?.internRole,
      item?.userRole,
      item?.intern?.role,
      nestedUser?.role,
      allowFallback ? fallback?.role : ''
    )
  );

  const rawUserId = pickFirstNonEmpty(
    docUserId,
    allowFallback ? fallbackUserId : ''
  );

  return {
    name,
    rollNo,
    role,
    userId: looksLikeObjectId(rawUserId) ? String(rawUserId) : '',
  };
}

function getTenantSlug() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    if (m) return decodeURIComponent(m[1]);
  } catch {
    // ignore
  }
  // ✅ fallback: critical after redirects that lose /t/:slug
  return readTenantSlugFromStorage();
}

function tenantUrl(p) {
  const slug = getTenantSlug();
  const path = String(p || '/');
  if (!slug) return path;
  if (!path.startsWith('/')) return `/t/${slug}/${path}`.replace(/\/{2,}/g, '/');
  if (path.startsWith(`/t/${slug}`)) return path;
  return `/t/${slug}${path}`.replace(/\/{2,}/g, '/');
}

function provisionalContextKey(templateId) {
  return `guidedActivityStart:${getTenantSlug() || 'default'}:${String(templateId || '')}`;
}

function guidedDateKey(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const direct = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (direct) return direct[1];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const VALIDATED_GUIDED_CENTRE_SOURCES = new Set([
  'current-posting-block',
  'roster-current-row+posting-block',
  'roster-current-row',
  'latest-roster-anchor+posting-block',
]);

function cachedGuidedCentreStillCurrent(payload = {}) {
  const context = payload?.__guidedCentreContext;
  const source = String(context?.source || '').trim();
  if (context?.validated !== true || !VALIDATED_GUIDED_CENTRE_SOURCES.has(source)) return false;
  if (!pickFirstNonEmpty(payload?.centre)) return false;

  const today = guidedDateKey(new Date());
  const fromDate = guidedDateKey(context?.fromDate);
  const toDate = guidedDateKey(context?.toDate);
  if (fromDate && today < fromDate) return false;
  if (toDate && today > toDate) return false;
  if (fromDate || toDate) return true;

  // If the backend did not provide posting bounds, keep reload safety to the
  // same calendar day as the authoritative launcher resolution.
  const asOfDate = guidedDateKey(context?.asOfDate || context?.resolvedAt);
  return Boolean(asOfDate && asOfDate === today);
}

function readReloadSafeStartPayload(templateId, navigationPayload) {
  const key = provisionalContextKey(templateId);
  try {
    if (navigationPayload) {
      // Only cache/accept centre context that the Activity Launcher already marked
      // as authoritative from the current posting resolver. Do not promote an
      // arbitrary navigation payload merely because it contains a centre value.
      const explicit = {
        ...navigationPayload,
        templateId: navigationPayload?.templateId || templateId,
      };

      if (cachedGuidedCentreStillCurrent(explicit)) {
        sessionStorage.setItem(key, JSON.stringify(explicit));
        return explicit;
      }

      // Keep non-centre launch metadata (template/citizen/date) but force the
      // existing safe launcher-return protection for an unvalidated/stale centre.
      sessionStorage.removeItem(key);
      const safe = { ...explicit };
      delete safe.centre;
      delete safe.__guidedCentreContext;
      return safe;
    }

    const stored = safeJsonParse(sessionStorage.getItem(key) || 'null');
    const storedIsGuidedContext =
      stored &&
      String(stored.templateId || '') === String(templateId || '') &&
      cachedGuidedCentreStillCurrent(stored);
    if (storedIsGuidedContext) return stored;

    // Direct/reload entry without an explicit or previously validated guided
    // handoff must not borrow generic browser centre state. The page will render
    // the existing "return to Activity Launcher" protection and no activity can
    // be created until a validated centre is supplied.
    const citizen = safeJsonParse(localStorage.getItem('activeCitizen') || sessionStorage.getItem('activeCitizen') || 'null');
    return {
      templateId,
      citizenId: citizen?.citizenId || undefined,
      citizenName: citizen?.name || citizen?.citizenName || undefined,
      date: new Date().toISOString(),
      sourceFlow: 'guided',
    };
  } catch {
    return { templateId, date: new Date().toISOString(), sourceFlow: 'guided' };
  }
}

// --- Auth + POST helper (to avoid owner-login redirects) ---
function listStorageKeys(store) {
  try {
    const out = [];
    const len = Number(store?.length || 0);
    for (let i = 0; i < len; i += 1) {
      const key = store.key(i);
      if (key) out.push(key);
    }
    return out;
  } catch {
    return [];
  }
}

function getAuthToken() {
  const pickFrom = (store) => {
    try {
      const keys = listStorageKeys(store);
      const preferred = [
        'cmfm_token',
        'cmfmToken',
        'tenant_token',
        'portal_token',
        'jwt',
        'token',
        'authToken',
      ];

      for (const name of preferred) {
        const hit = keys.find((x) => String(x).toLowerCase() === String(name).toLowerCase());
        if (hit) return store.getItem(hit);
      }

      const nonOwner = keys.find((key) => /token/i.test(key) && !/owner/i.test(key) && !/citizen/i.test(key));
      if (nonOwner) return store.getItem(nonOwner);

      const any = keys.find((key) => /token/i.test(key) && !/owner/i.test(key));
      return any ? store.getItem(any) : null;
    } catch {
      return null;
    }
  };

  return pickFrom(localStorage) || pickFrom(sessionStorage);
}

function looksLikeObjectId(v) {
  const s = String(v || '').trim();
  return /^[a-fA-F0-9]{24}$/.test(s);
}

function readTenantIdFromStorage() {
  try {
    const candidates = [];
    const t1 = localStorage.getItem('tenantId');
    const t2 = localStorage.getItem('activeTenantId');
    const t3 = sessionStorage.getItem('tenantId');
    const t4 = sessionStorage.getItem('activeTenantId');

    [t1, t2, t3, t4].filter(Boolean).forEach((x) => candidates.push(x));

    const lu = safeJsonParse(localStorage.getItem('loggedInUser') || 'null');
    const su = safeJsonParse(sessionStorage.getItem('loggedInUser') || 'null');

    [lu, su].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(
        core?.tenantId,
        obj?.tenantId,
        core?.tenant?._id,
        core?.tenant?.id,
        obj?.tenant?._id,
        obj?.tenant?.id
      );
    });

    const hit = candidates
      .map((x) => String(x || '').trim())
      .find((x) => looksLikeObjectId(x));

    return hit || '';
  } catch {
    return '';
  }
}

function buildAuthHeaders(extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  const t = getAuthToken();
  if (t) headers.Authorization = `Bearer ${t}`;

  const slug = getTenantSlug();
  if (slug) {
    headers['X-Tenant-Slug'] = slug;

    // ✅ NO REGRESSION + FIX:
    // Do NOT set X-Tenant-Id to the slug.
    // Only set X-Tenant-Id if we actually have a real ObjectId stored.
    const tenantId = readTenantIdFromStorage();
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
  }

  headers['X-Requested-From'] = 'portal-tenant';
  return headers;
}

// ✅ Ensure submit requests hit backend (dev: 5000) not React dev server (3000)
function getApiBase() {
  try {
    const env =
      (typeof process !== 'undefined' &&
        process?.env &&
        (process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL)) ||
      '';
    if (env) return String(env).replace(/\/+$/, '');

    const origin = (window.location && window.location.origin) ? window.location.origin : '';
    if (!origin) return '';
    // dev fallback: 3000 -> 5000
    if (origin.includes(':3000')) return origin.replace(':3000', ':5000');
    return origin;
  } catch {
    return '';
  }
}

function buildApiUrl(path) {
  const base = getApiBase();
  const fullPath = tenantUrl(path);
  if (!base) return fullPath;
  return `${base}${fullPath}`.replace(/\/{2,}/g, '/').replace(':/', '://');
}

// ✅ Try multiple submit endpoints (guided activity first, then legacy aliases)
async function submitViaTenantEndpoints(id) {
  const candidates = [
    `/api/activities/${id}/submit-observation`,
    `/api/activities/${id}/submit`,
    `/api/activity-instances/${id}/submit-observation`,
    `/api/activity-instances/${id}/submit`,
    `/api/internActivities/${id}/submit`,
    `/api/intern-activities/${id}/submit`,
  ];

  let lastErr = null;

  for (const path of candidates) {
    try {
      const res = await fetch(buildApiUrl(path), {
        method: 'POST',
        headers: buildAuthHeaders(),
        credentials: 'include',
      });
      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        const e = new Error(
          json?.message || json?.error || `Submit failed: ${res.status}`
        );
        e.status = res.status;
        e.code = json?.code || '';
        e.missingSteps = Array.isArray(json?.missingSteps)
          ? json.missingSteps
          : (Array.isArray(json?.issues) ? json.issues : []);

        // Any coded activity-domain failure is a real business/validation
        // response, not an endpoint mismatch. Never fall through to a legacy
        // submit alias after the canonical backend has deliberately rejected it.
        if (String(e.code || '').startsWith('ACTIVITY_')) {
          throw e;
        }
        throw e;
      }

      return json;
    } catch (e) {
      lastErr = e;

      if (String(e?.code || '').startsWith('ACTIVITY_')) {
        throw e;
      }

      // Preserve compatibility probing for older route variants.
      // Only 400/404 responses are treated as potential endpoint mismatches.
      if (e.status !== 400 && e.status !== 404) {
        throw e;
      }
    }
  }

  if (lastErr) {
    lastErr.endpointFallbackEligible = lastErr.status === 400 || lastErr.status === 404;
    throw lastErr;
  }

  const e = new Error('Submit endpoint not found');
  e.status = 404;
  e.endpointFallbackEligible = true;
  throw e;
}

// ✅ tenant-aware GET fallback to fix "Route not found: GET /api/intern-activities/:id"
async function getActivityViaTenantEndpoints(id) {
  const candidates = [
    `/api/activities/${id}`,
    `/api/activity-instances/${id}`,
    `/api/internActivities/${id}`,
    `/api/intern-activities/${id}`,
  ];

  let lastErr = null;

  for (const path of candidates) {
    try {
      const res = await fetch(buildApiUrl(path), {
        method: 'GET',
        headers: buildAuthHeaders(),
        credentials: 'include',
      });
      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        const e = new Error(
          json?.message || json?.error || `Load failed: ${res.status}`
        );
        e.status = res.status;
        throw e;
      }

      return json;
    } catch (e) {
      lastErr = e;
      // For 400/404 we try the next candidate; for auth/other errors we bubble up.
      if (e.status !== 400 && e.status !== 404) throw e;
    }
  }

  if (lastErr) throw lastErr;
  throw new Error('Activity endpoint not found');
}

// ---------- Structured field definitions ----------
// New templates can define `step.fields` in ActivityTemplate. Those definitions
// are rendered generically below and stored inside the existing step.valueText
// JSON envelope, so historical ActivityInstance persistence remains compatible.
//
// The NVD map is retained as a legacy fallback for already-seeded templates that
// pre-date template-driven fields. New activities should prefer template fields.

const STRUCTURED_MARKER = '__structured__';

const STRUCTURED_FIELDS = {
  // slug: nvd (Conduct Normal Delivery)
  nvd: {
    1: [
      { key: 'motherName', label: "Mother's name", type: 'text' },
      { key: 'age', label: 'Age (years)', type: 'number' },
      { key: 'address', label: 'Address', type: 'textarea' },
      { key: 'gravidaG', label: 'Gravida (G)', type: 'number' },
      { key: 'paraP', label: 'Para (P)', type: 'number' },
      { key: 'livingL', label: 'Living (L)', type: 'number' },
      { key: 'abortionsA', label: 'Abortions (A)', type: 'number' },
      { key: 'mctcNo', label: 'MCTC / MCTS number', type: 'text' },
      { key: 'contactNumber', label: 'Contact number', type: 'text' },
      { key: 'birthCompanionName', label: 'Birth companion name', type: 'text' },
      { key: 'birthCompanionRelation', label: 'Birth companion relation', type: 'text' },
      { key: 'ashaName', label: 'ASHA name', type: 'text' },
    ],
    2: [
      { key: 'lmpDate', label: 'LMP (date)', type: 'date' },
      { key: 'eddDate', label: 'EDD (date)', type: 'date' },
      { key: 'usgDate', label: 'Last USG date', type: 'date' },
      { key: 'usgGA', label: 'USG GA (weeks)', type: 'number' },
      { key: 'usgPlacenta', label: 'Placenta (location/notes)', type: 'text' },
      { key: 'usgLiquor', label: 'Liquor (AFI/notes)', type: 'text' },
      { key: 'usgAnomalies', label: 'USG anomalies (if any)', type: 'textarea' },
      { key: 'immunisationStatus', label: 'Immunisation status (TT/Td)', type: 'text' },
      { key: 'lastHb', label: 'Last Hb (g/dL)', type: 'number' },
      { key: 'bloodGroup', label: 'Blood group', type: 'text' },
      { key: 'rhFactor', label: 'Rh factor', type: 'text' },
      { key: 'hivStatus', label: 'HIV status', type: 'text' },
      { key: 'hbsagStatus', label: 'HBsAg status', type: 'text' },
      { key: 'hcvStatus', label: 'HCV status', type: 'text' },
      { key: 'rbs', label: 'Random blood sugar (mg/dL)', type: 'number' },
      { key: 'vdrl', label: 'VDRL result', type: 'text' },
      { key: 'urineSugar', label: 'Urine sugar', type: 'text' },
      { key: 'urineProtein', label: 'Urine protein', type: 'text' },
      { key: 'urineMicroscopy', label: 'Urine microscopy', type: 'textarea' },
    ],
    3: [
      { key: 'bp', label: 'BP (mmHg)', type: 'text' },
      { key: 'pulse', label: 'Pulse (per min)', type: 'number' },
      { key: 'temperature', label: 'Temperature (°C)', type: 'number' },
      { key: 'respRate', label: 'Respiratory rate (per min)', type: 'number' },
      { key: 'spo2', label: 'SpO₂ (%)', type: 'number' },
      { key: 'pallorOedema', label: 'Pallor/oedema', type: 'textarea' },
      { key: 'fundalHeight', label: 'Fundal height (cm)', type: 'number' },
      { key: 'liePresentation', label: 'Lie & presentation', type: 'text' },
      { key: 'fhr', label: 'Fetal heart rate (bpm)', type: 'number' },
      { key: 'dangerSigns', label: 'Danger signs (if any)', type: 'textarea' },
    ],
    4: [
      { key: 'cervixDil', label: 'Cervical dilatation (cm)', type: 'number' },
      { key: 'effacement', label: 'Effacement (%)', type: 'number' },
      { key: 'station', label: 'Station', type: 'text' },
      { key: 'membranesStatus', label: 'Membrane status', type: 'text' },
      { key: 'ruptureTime', label: 'Time of rupture (if any)', type: 'text' },
      { key: 'liquorColour', label: 'Liquor colour', type: 'text' },
      { key: 'contractionFreq', label: 'Contractions (frequency)', type: 'text' },
      { key: 'contractionDuration', label: 'Contractions (duration)', type: 'text' },
      { key: 'partographStarted', label: 'Partograph started/updated', type: 'textarea' },
    ],
    5: [
      { key: 'maternalMonitoring', label: 'Maternal monitoring notes', type: 'textarea' },
      { key: 'fhrMonitoring', label: 'FHR monitoring notes', type: 'textarea' },
      { key: 'progressNotes', label: 'Progress & alerts', type: 'textarea' },
    ],
    6: [
      { key: 'timeFullDil', label: 'Time of full dilatation', type: 'text' },
      { key: 'timeBearingDown', label: 'Time of onset of bearing down', type: 'text' },
      { key: 'modeOfDelivery', label: 'Mode of delivery', type: 'text' },
      { key: 'episiotomy', label: 'Episiotomy (yes/no & type)', type: 'text' },
      { key: 'perinealTearDegree', label: 'Perineal tear (degree)', type: 'text' },
      { key: 'intrapartumComplications', label: 'Intrapartum complications', type: 'textarea' },
    ],
    7: [
      { key: 'babyCry', label: 'Baby cried at birth?', type: 'text' },
      { key: 'resuscitation', label: 'Resuscitation (if any)', type: 'textarea' },
      { key: 'apgar1', label: 'APGAR at 1 min', type: 'number' },
      { key: 'apgar5', label: 'APGAR at 5 min', type: 'number' },
      { key: 'skinToSkin', label: 'Skin-to-skin given?', type: 'text' },
      { key: 'breastfeeding1h', label: 'Breastfeeding within 1 hour?', type: 'text' },
    ],
    8: [
      { key: 'uterotonic', label: 'Uterotonic (drug & dose)', type: 'text' },
      { key: 'uterotonicTime', label: 'Time uterotonic given', type: 'text' },
      { key: 'cctDone', label: 'Controlled cord traction done?', type: 'text' },
      { key: 'uterineMassage', label: 'Uterine massage done?', type: 'text' },
      { key: 'pph', label: 'PPH occurred?', type: 'text' },
      { key: 'pphManagement', label: 'PPH management (if any)', type: 'textarea' },
    ],
    9: [
      { key: 'placentaComplete', label: 'Placenta complete?', type: 'text' },
      { key: 'membranesComplete', label: 'Membranes complete?', type: 'text' },
      { key: 'retainedBits', label: 'Retained bits / abnormalities', type: 'textarea' },
      { key: 'perineumFindings', label: 'Perineum findings', type: 'textarea' },
    ],
    10: [
      { key: 'postpartumVitals', label: 'Postpartum vitals notes', type: 'textarea' },
      { key: 'uterineTone', label: 'Uterine tone notes', type: 'textarea' },
      { key: 'lochia', label: 'Vaginal bleeding/lochia', type: 'textarea' },
      { key: 'postpartumComplications', label: 'Postpartum complications', type: 'textarea' },
    ],
    11: [
      { key: 'newbornTemp', label: 'Newborn temperature / condition', type: 'textarea' },
      { key: 'feedingStatus', label: 'Feeding status', type: 'text' },
      { key: 'vitaminK', label: 'Vitamin K given?', type: 'text' },
      { key: 'eyeProphylaxis', label: 'Eye prophylaxis given?', type: 'text' },
      { key: 'otherProphylaxis', label: 'Other prophylaxis', type: 'textarea' },
    ],
    12: [
      { key: 'counsellingDangerSigns', label: 'Counselling on danger signs', type: 'textarea' },
      { key: 'counsellingBreastfeeding', label: 'Counselling on breastfeeding', type: 'textarea' },
      { key: 'counsellingHygiene', label: 'Counselling on hygiene/cord care', type: 'textarea' },
      { key: 'counsellingFP', label: 'Postpartum family planning counselling', type: 'textarea' },
      { key: 'followupPlan', label: 'Follow-up schedule/plan', type: 'textarea' },
    ],
    // Step 13 already has a numeric count field via evidenceType: 'count'.
  },
};

function getTemplateSlug(tplOrSlug) {
  if (!tplOrSlug) return '';
  if (typeof tplOrSlug === 'string') return tplOrSlug.toLowerCase();
  return String(tplOrSlug.slug || '').toLowerCase();
}

function normalizeStructuredField(field = {}) {
  const type = normalizeEvidenceTypeForRender(field.type || 'text');
  const options = normalizeStepOptions(field);
  return {
    ...(field || {}),
    key: sanitizeDisplayText(field.key || ''),
    label: sanitizeDisplayText(field.label || field.key || ''),
    type: type === 'none' ? 'text' : type,
    placeholder: sanitizeDisplayText(field.placeholder || ''),
    helpText: sanitizeDisplayText(field.helpText || ''),
    unit: sanitizeDisplayText(field.unit || ''),
    options,
    visibleForModes: Array.isArray(field.visibleForModes)
      ? field.visibleForModes.map(normalizeParticipationMode).filter(Boolean)
      : [],
    requiredForModes: Array.isArray(field.requiredForModes)
      ? field.requiredForModes.map(normalizeParticipationMode).filter(Boolean)
      : [],
    requiredForComponents: normalizeParticipationComponents(field.requiredForComponents),
    showWhen: field.showWhen && typeof field.showWhen === 'object'
      ? {
          ...field.showWhen,
          stepOrder: Number(field.showWhen.stepOrder || 0) || undefined,
          fieldKey: sanitizeDisplayText(field.showWhen.fieldKey || ''),
          operator: String(field.showWhen.operator || 'in').trim().toLowerCase(),
          values: normalizeParticipationComponents(field.showWhen.values),
        }
      : null,
  };
}

function getStructuredFields(tplOrSlug, order) {
  if (tplOrSlug && typeof tplOrSlug === 'object') {
    const step = (Array.isArray(tplOrSlug.steps) ? tplOrSlug.steps : [])
      .find((item) => Number(item?.order) === Number(order));
    const fields = Array.isArray(step?.fields)
      ? step.fields.map(normalizeStructuredField).filter((field) => field.key && field.label)
      : [];
    if (fields.length) return fields;
  }

  const slug = getTemplateSlug(tplOrSlug);
  const bySlug = STRUCTURED_FIELDS[slug];
  if (!bySlug) return [];
  return (bySlug[order] || []).map(normalizeStructuredField);
}

function decodeStructured(tplOrSlug, order, valueText) {
  const fields = getStructuredFields(tplOrSlug, order);
  if (!fields.length) return { structured: {}, notes: valueText || '' };
  if (!valueText || typeof valueText !== 'string') return { structured: {}, notes: '' };

  try {
    const parsed = JSON.parse(valueText);
    if (parsed && typeof parsed === 'object' && parsed[STRUCTURED_MARKER]) {
      const body = parsed[STRUCTURED_MARKER] || {};
      const { __notes, ...rest } = body;
      return { structured: rest, notes: __notes || '' };
    }
  } catch {
    // fall through to plain text
  }
  return { structured: {}, notes: valueText || '' };
}

function encodeStructured(tplOrSlug, order, structured, notes) {
  const fields = getStructuredFields(tplOrSlug, order);
  if (!fields.length) {
    // No special structure; keep notes as plain text
    return notes || '';
  }
  const payload = { ...(structured || {}) };
  if (notes) payload.__notes = notes;
  return JSON.stringify({ [STRUCTURED_MARKER]: payload });
}

// ---------- Generic evidence renderer helpers ----------
// ActivityTemplate already supports these evidence types in the backend model.
// Keep persistence backward-compatible by reusing the existing stepData fields:
// - valueText  -> text/textarea/date/select/multiselect/file reference
// - valueCount -> count/number
// - valueBool  -> checkbox/boolean
// - photos     -> photo URLs
// This avoids a backend/schema change while allowing template-driven rendering.
const TOP_LEVEL_COUNT_KEYS = [
  'consultations',
  'procedures',
  'immunizations',
  'observed',
  'performed',
];

function normalizeEvidenceTypeForRender(value) {
  const raw = String(value || 'none')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

  if (!raw || raw === 'none' || raw === 'no evidence' || raw === 'nil') return 'none';
  if (['text', 'short text'].includes(raw)) return 'text';
  if (['textarea', 'long text', 'note', 'notes'].includes(raw)) return 'textarea';
  if (raw === 'count') return 'count';
  if (['number', 'numeric'].includes(raw)) return 'number';
  if (['checkbox', 'check box'].includes(raw)) return 'checkbox';
  if (['boolean', 'bool', 'yes no', 'yes/no'].includes(raw)) return 'boolean';
  if (['photo', 'image', 'picture'].includes(raw)) return 'photo';
  if (['file', 'attachment', 'document'].includes(raw)) return 'file';
  if (['date', 'datetime', 'time'].includes(raw)) return 'date';
  if (['select', 'dropdown', 'single select'].includes(raw)) return 'select';
  if (['multiselect', 'multi select', 'multiple select'].includes(raw)) return 'multiselect';
  return 'none';
}

function normalizeStepOptions(step = {}) {
  const values = Array.isArray(step.options) ? step.options : [];
  return values
    .map((option) => {
      if (option && typeof option === 'object' && !Array.isArray(option)) {
        const value = pickFirstNonEmpty(option.value, option.label);
        if (!value) return null;
        const label = pickFirstNonEmpty(option.label, option.value, value);
        return { value: String(value), label: sanitizeDisplayText(label) };
      }
      const value = String(option ?? '').trim();
      return value ? { value, label: sanitizeDisplayText(value) } : null;
    })
    .filter(Boolean);
}

function parseMultiselectValue(valueText) {
  if (Array.isArray(valueText)) return valueText.map((value) => String(value));
  const raw = String(valueText || '').trim();
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((value) => String(value)).filter(Boolean);
    }
  } catch {
    // Backward-compatible fallback for older comma/newline/pipe separated text.
  }

  return raw
    .split(/[|,\n]/g)
    .map((value) => String(value || '').trim())
    .filter(Boolean);
}

function encodeMultiselectValue(values) {
  const normalized = Array.isArray(values)
    ? [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))]
    : [];
  return normalized.length ? JSON.stringify(normalized) : '';
}

function hasMeaningfulEvidenceValue(value) {
  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some((item) => hasMeaningfulEvidenceValue(item));
  if (typeof value === 'object') {
    return Object.values(value).some((item) => hasMeaningfulEvidenceValue(item));
  }
  return false;
}

function readStructuredFieldValue(sourceAnswers = [], stepOrder, fieldKey) {
  const answer = (sourceAnswers || []).find(
    (item) => Number(item?.order) === Number(stepOrder)
  );
  return answer?.structured?.[fieldKey];
}

function imnciUrgentFindingReasons(template = {}, sourceAnswers = []) {
  if (getTemplateSlug(template) !== 'imnci-under5') return [];

  const reasons = [];
  const dangerSigns = readStructuredFieldValue(sourceAnswers, 2, 'generalDangerSigns');
  const meaningfulDangerSigns = (Array.isArray(dangerSigns) ? dangerSigns : [])
    .map((value) => String(value || '').trim())
    .filter((value) => value && value !== 'none_identified');
  if (meaningfulDangerSigns.length) reasons.push('general danger sign recorded');

  if (readStructuredFieldValue(sourceAnswers, 3, 'chestIndrawing') === true) {
    reasons.push('chest indrawing recorded');
  }
  if (readStructuredFieldValue(sourceAnswers, 3, 'stridor') === true) {
    reasons.push('stridor recorded');
  }
  if (readStructuredFieldValue(sourceAnswers, 6, 'mastoidTenderSwelling') === true) {
    reasons.push('tender swelling behind the ear recorded');
  }
  if (readStructuredFieldValue(sourceAnswers, 7, 'urgentReferral') === true) {
    reasons.push('young-infant urgent referral indicated');
  }
  if (readStructuredFieldValue(sourceAnswers, 8, 'bilateralPedalOedema') === true) {
    reasons.push('bilateral pedal oedema recorded');
  }

  return [...new Set(reasons)];
}

function validateImnciClinicalConsistency(template = {}, sourceAnswers = []) {
  const reasons = imnciUrgentFindingReasons(template, sourceAnswers);
  if (!reasons.length) return [];

  const reasonText = reasons.join(', ');
  const issues = [];
  const escalation = readStructuredFieldValue(sourceAnswers, 2, 'seniorEscalation');
  const severeClassification = readStructuredFieldValue(
    sourceAnswers,
    12,
    'severeClassificationPresent'
  );
  const referral = readStructuredFieldValue(sourceAnswers, 14, 'referralRequired');

  if (String(escalation || '').trim() === 'not_indicated') {
    issues.push({
      order: 2,
      fieldKey: 'seniorEscalation',
      title: 'Immediate danger-sign / serious-illness screen — Senior escalation',
      evidenceType: 'select',
      reason: `Urgent/severe finding already recorded (${reasonText}). “Not indicated” is clinically inconsistent unless the underlying findings are revised.`,
      clinicalConsistency: true,
    });
  }

  if (severeClassification === false) {
    issues.push({
      order: 12,
      fieldKey: 'severeClassificationPresent',
      title: 'Consolidated IMNCI classification — Severe classification / danger sign affecting urgency?',
      evidenceType: 'boolean',
      reason: `Urgent/severe finding already recorded (${reasonText}). “No” is clinically inconsistent unless the underlying findings are revised.`,
      clinicalConsistency: true,
    });
  }

  if (String(referral || '').trim() === 'no') {
    issues.push({
      order: 14,
      fieldKey: 'referralRequired',
      title: 'Referral, pre-referral care and safe escalation — Referral / urgent senior review required?',
      evidenceType: 'select',
      reason: `Urgent/severe finding already recorded (${reasonText}). “No” is clinically inconsistent unless the underlying findings are revised.`,
      clinicalConsistency: true,
    });
  }

  return issues;
}

function structuredConditionMatches(
  field = {},
  currentStepOrder,
  sourceAnswers = []
) {
  const condition = field?.showWhen;
  if (!condition || typeof condition !== 'object' || !condition.fieldKey) return true;

  const sourceOrder = Number(condition.stepOrder || currentStepOrder);
  const rawValue = readStructuredFieldValue(
    sourceAnswers,
    sourceOrder,
    condition.fieldKey
  );
  const values = Array.isArray(rawValue)
    ? rawValue.map((value) => String(value ?? '').trim())
    : [String(rawValue ?? '').trim()];
  const wanted = normalizeParticipationComponents(condition.values);
  const operator = String(condition.operator || 'in').trim().toLowerCase();

  if (operator === 'truthy') {
    return Array.isArray(rawValue)
      ? rawValue.length > 0
      : hasMeaningfulEvidenceValue(rawValue);
  }
  if (operator === 'falsy') {
    return Array.isArray(rawValue)
      ? rawValue.length === 0
      : !hasMeaningfulEvidenceValue(rawValue);
  }

  const hasMatch = values.some((value) => wanted.includes(value));
  if (operator === 'not-in' || operator === 'not-equals') return !hasMatch;
  return hasMatch;
}

function isStructuredFieldVisible(
  field = {},
  currentStepOrder,
  participationModeValue = '',
  sourceAnswers = []
) {
  const mode = normalizeParticipationMode(participationModeValue);
  const visibleForModes = Array.isArray(field.visibleForModes)
    ? field.visibleForModes.map(normalizeParticipationMode).filter(Boolean)
    : [];

  if (mode && visibleForModes.length && !visibleForModes.includes(mode)) {
    return false;
  }

  return structuredConditionMatches(field, currentStepOrder, sourceAnswers);
}

function isStructuredFieldRequired(
  field = {},
  participationModeValue = '',
  selectedComponents = []
) {
  const mode = normalizeParticipationMode(participationModeValue);
  const requiredForModes = Array.isArray(field.requiredForModes)
    ? field.requiredForModes.map(normalizeParticipationMode).filter(Boolean)
    : [];
  const requiredForComponents = normalizeParticipationComponents(
    field.requiredForComponents
  );
  const selected = normalizeParticipationComponents(selectedComponents);

  const modeConfigured = requiredForModes.length > 0;
  const componentConfigured = requiredForComponents.length > 0;
  const modeMatches = !modeConfigured || (!!mode && requiredForModes.includes(mode));
  const componentMatches =
    !componentConfigured ||
    requiredForComponents.some((component) => selected.includes(component));

  if (modeConfigured || componentConfigured) {
    return modeMatches && componentMatches;
  }

  return field?.required === true;
}

function structuredFieldValueIssue(field = {}, value) {
  const type = normalizeEvidenceTypeForRender(field.type || 'text');

  if (type === 'multiselect') {
    if (!Array.isArray(value) || value.length === 0) return 'Select at least one option.';
    return '';
  }

  if (type === 'boolean') {
    if (typeof value !== 'boolean') return 'Select Yes or No.';
    return '';
  }

  if (type === 'checkbox') {
    if (value !== true) return 'Complete this required confirmation.';
    return '';
  }

  if (type === 'number') {
    const number = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(number)) return 'Enter a valid number.';
    const hasMin = field.min !== undefined && field.min !== null && field.min !== '';
    const hasMax = field.max !== undefined && field.max !== null && field.max !== '';
    const min = hasMin ? Number(field.min) : null;
    const max = hasMax ? Number(field.max) : null;
    if (hasMin && Number.isFinite(min) && number < min) {
      return `Enter a value of at least ${field.min}.`;
    }
    if (hasMax && Number.isFinite(max) && number > max) {
      return `Enter a value no greater than ${field.max}.`;
    }
    return '';
  }

  if (!hasMeaningfulEvidenceValue(value)) return 'Enter the required value.';
  return '';
}

function validateStructuredFields(
  template = {},
  sourceAnswers = [],
  participationModeValue = '',
  selectedComponents = []
) {
  const issues = [];
  const steps = Array.isArray(template?.steps) ? template.steps : [];

  steps.forEach((step, index) => {
    const stepOrder = Number(step?.order || index + 1);
    const fields = getStructuredFields(template, stepOrder);
    if (!fields.length) return;

    fields.forEach((field) => {
      if (!isStructuredFieldVisible(
        field,
        stepOrder,
        participationModeValue,
        sourceAnswers
      )) {
        return;
      }
      if (!isStructuredFieldRequired(
        field,
        participationModeValue,
        selectedComponents
      )) {
        return;
      }

      const value = readStructuredFieldValue(
        sourceAnswers,
        stepOrder,
        field.key
      );
      const reason = structuredFieldValueIssue(field, value);
      if (!reason) return;

      issues.push({
        order: stepOrder,
        key: String(step?.key || '').trim(),
        fieldKey: field.key,
        title: sanitizeDisplayText(
          `${step?.title || `Step ${stepOrder}`} — ${field.label}`
        ),
        evidenceType: normalizeEvidenceTypeForRender(field.type || 'text'),
        reason,
      });
    });
  });

  return issues;
}

function stepAnswerForValidation(step = {}, sourceAnswers = []) {
  const stepOrder = Number(step.order);
  const byOrder = (sourceAnswers || []).find((answer) => Number(answer?.order) === stepOrder);
  if (byOrder) return byOrder;

  const key = String(step.key || '').trim();
  if (!key) return {};
  return (sourceAnswers || []).find((answer) => String(answer?.key || '').trim() === key) || {};
}

function numberValidationIssue(step = {}, answer = {}) {
  const raw = answer?.valueCount;
  const value = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(value)) return 'Enter a valid number.';

  const hasMin = step.min !== undefined && step.min !== null && step.min !== '';
  const hasMax = step.max !== undefined && step.max !== null && step.max !== '';
  const min = hasMin ? Number(step.min) : null;
  const max = hasMax ? Number(step.max) : null;

  if (hasMin && Number.isFinite(min) && value < min) return `Enter a value of at least ${step.min}.`;
  if (hasMax && Number.isFinite(max) && value > max) return `Enter a value no greater than ${step.max}.`;
  return '';
}

function validateRequiredActivityEvidence(
  template = {},
  sourceAnswers = [],
  participationModeValue = '',
  selectedComponents = []
) {
  const issues = [];
  const steps = Array.isArray(template?.steps) ? template.steps : [];
  const roleAware = isRoleAwareTemplate(template);
  const participationMode = normalizeParticipationMode(participationModeValue);

  steps.forEach((step, index) => {
    if (!isStepVisibleForMode(step, participationMode)) return;
    if (!isStepRequiredForMode(step, participationMode)) return;

    const evidenceType = normalizeEvidenceTypeForRender(step.evidenceType);
    if (evidenceType === 'none') {
      if (roleAware && participationMode) {
        const order = Number.isFinite(Number(step.order)) ? Number(step.order) : index + 1;
        issues.push({
          order,
          key: String(step.key || '').trim(),
          title: sanitizeDisplayText(step.title || step.label || `Step ${order}`),
          evidenceType,
          reason: 'Required evidence is not configured for this step. Contact an administrator.',
          configurationError: true,
        });
      }
      return;
    }

    const answer = stepAnswerForValidation(step, sourceAnswers);
    const order = Number.isFinite(Number(step.order)) ? Number(step.order) : index + 1;
    const title = sanitizeDisplayText(step.title || step.label || `Step ${order}`);
    let reason = '';

    if (evidenceType === 'text' || evidenceType === 'textarea') {
      const structuredFields = getStructuredFields(template, step.order);
      const templateDrivenFields = Array.isArray(step?.fields) && step.fields.length > 0;
      const hasStructuredEvidence = structuredFields.length > 0 &&
        hasMeaningfulEvidenceValue(answer?.structured || {});
      const hasNotes = String(answer?.valueText || '').trim().length > 0;

      // Template-driven fields have their own field-level validation below.
      // Legacy structured maps (NVD) keep the historical "any structured
      // evidence or notes" step-level requirement.
      if (!templateDrivenFields && !hasStructuredEvidence && !hasNotes) {
        reason = 'Enter the required evidence.';
      }
    } else if (['date', 'select', 'file'].includes(evidenceType)) {
      if (!String(answer?.valueText || '').trim()) reason = 'Enter the required evidence.';
    } else if (evidenceType === 'multiselect') {
      if (!parseMultiselectValue(answer?.valueText).length) reason = 'Select at least one option.';
    } else if (evidenceType === 'count' || evidenceType === 'number') {
      reason = numberValidationIssue(step, answer);
    } else if (evidenceType === 'boolean') {
      if (typeof answer?.valueBool !== 'boolean') reason = 'Select Yes or No.';
    } else if (evidenceType === 'checkbox') {
      if (answer?.valueBool !== true) reason = 'Complete this required confirmation.';
    } else if (evidenceType === 'photo') {
      const photos = Array.isArray(answer?.photos) ? answer.photos : [];
      if (!photos.some((value) => String(value || '').trim())) reason = 'Add the required photo evidence.';
    }

    if (reason) {
      issues.push({
        order,
        key: String(step.key || answer?.key || '').trim(),
        title,
        evidenceType,
        reason,
      });
    }
  });

  const structuredIssues = validateStructuredFields(
    template,
    sourceAnswers,
    participationModeValue,
    selectedComponents
  );
  const clinicalConsistencyIssues = validateImnciClinicalConsistency(
    template,
    sourceAnswers
  );

  return [...issues, ...structuredIssues, ...clinicalConsistencyIssues];
}

function validationSummary(issues = []) {
  const clinicalIssues = (issues || []).filter((issue) => issue?.clinicalConsistency);
  if (clinicalIssues.length) {
    const labels = clinicalIssues
      .slice(0, 4)
      .map((issue) => `${issue.order}. ${issue.title}`);
    const extra = Math.max(0, clinicalIssues.length - labels.length);
    return `Resolve clinical consistency checks before submitting: ${labels.join('; ')}${extra ? `; +${extra} more` : ''}.`;
  }

  const configurationIssues = (issues || []).filter((issue) => issue?.configurationError);
  if (configurationIssues.length) {
    const labels = configurationIssues
      .slice(0, 4)
      .map((issue) => `${issue.order}. ${issue.title}`);
    const extra = Math.max(0, configurationIssues.length - labels.length);
    return `Activity template configuration must be corrected before submission: ${labels.join('; ')}${extra ? `; +${extra} more` : ''}.`;
  }

  const labels = (issues || [])
    .slice(0, 6)
    .map((issue) => `${issue.order}. ${issue.title}`);
  const extra = Math.max(0, (issues || []).length - labels.length);
  return `Complete required evidence before submitting: ${labels.join('; ')}${extra ? `; +${extra} more` : ''}.`;
}

function stepHelperText(step = {}, fallback = '') {
  const details = [];
  const helpText = sanitizeDisplayText(step.helpText || '');
  const unit = sanitizeDisplayText(step.unit || '');
  if (helpText) details.push(helpText);
  if (unit) details.push(`Unit: ${unit}`);

  const hasMin = step.min !== undefined && step.min !== null && step.min !== '';
  const hasMax = step.max !== undefined && step.max !== null && step.max !== '';
  if (hasMin && hasMax) details.push(`Allowed range: ${step.min}–${step.max}`);
  else if (hasMin) details.push(`Minimum: ${step.min}`);
  else if (hasMax) details.push(`Maximum: ${step.max}`);

  if (!details.length && fallback) details.push(fallback);
  return details.join(' • ');
}

function numericInputProps(step = {}) {
  const props = {};
  if (step.min !== undefined && step.min !== null && step.min !== '') props.min = step.min;
  if (step.max !== undefined && step.max !== null && step.max !== '') props.max = step.max;
  return props;
}

function fixedNumericStepValue(step = {}) {
  const evidenceType = normalizeEvidenceTypeForRender(step.evidenceType);
  if (!['count', 'number'].includes(evidenceType)) return undefined;

  const min = Number(step.min);
  const max = Number(step.max);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min !== max) {
    return undefined;
  }

  return min;
}

function hasRecordedTopLevelCounts(values = {}) {
  return TOP_LEVEL_COUNT_KEYS.some((key) => Number(values?.[key] || 0) !== 0);
}

function normalizeActivityKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[_/()–—-]+/g, ' ')
    .replace(/[^a-z0-9\s]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isOpdHandoffTemplate(template = {}) {
  const identities = [
    template.slug,
    template.templateSlug,
    template.activityKey,
    template.title,
    template.templateTitle,
    template.name,
    template.templateName,
  ].map(normalizeActivityKey);
  const sourceFlow = normalizeActivityKey(template.sourceFlow);
  return identities.includes('opd primary care') ||
    identities.includes('opd primary care consult phc uphc chc') ||
    sourceFlow === 'opd' || sourceFlow === 'primary care' || sourceFlow === 'opd primary care';
}

const CPA_OPD = `${CORE_PRACTICE_AREA_LABEL} — OPD consultation (assessment → plan → referral)`;
const CPA_MNCH = `${CORE_PRACTICE_AREA_LABEL} — MNCH day (ANC/PNC/Newborn/Under-5)`;
const CPA_IMMUNIZATION = `${CORE_PRACTICE_AREA_LABEL} — Immunization session (service delivery + safety)`;
const CPA_PROGRAMME = `${CORE_PRACTICE_AREA_LABEL} — Programme & outreach activities`;

function buildGuidedReportingTarget({ template = {}, doc = {} } = {}) {
  const slug = normalizeActivityKey(
    pickFirstNonEmpty(
      template.slug,
      doc.templateSlug,
      doc.activityKey,
      template.title,
      template.name,
      doc.templateTitle,
      doc.templateName
    )
  );

  if (slug.includes('nvd') || slug.includes('normal vaginal delivery') || slug.includes('normal delivery')) {
    return {
      section: CPA_MNCH,
      item: 'Conduct Normal vaginal delivery',
      metric: 'performed',
      source: 'mapped',
    };
  }

  if (slug.includes('imnci') || slug.includes('under 5') || slug.includes('under5') || slug.includes('sick child')) {
    return {
      section: CPA_MNCH,
      item: 'IMNCI: management of childhood illnesses',
      metric: 'performed',
      source: 'mapped',
    };
  }

  if (slug.includes('immunization') || slug.includes('immunisation') || slug.includes('uip')) {
    return {
      section: CPA_IMMUNIZATION,
      item: 'Immunization session / service delivery',
      metric: 'performed',
      source: 'mapped',
    };
  }

  if (slug.includes('opd') || slug.includes('primary care') || slug.includes('common ailment')) {
    return {
      section: CPA_OPD,
      item: 'Diagnose & Manage Common Ailments',
      metric: 'performed',
      source: 'mapped',
    };
  }

  if (slug.includes('tb') || slug.includes('ntep')) {
    return {
      section: CPA_PROGRAMME,
      item: 'TB Screening/Notification & Contact Management (NTEP)',
      metric: 'performed',
      source: 'mapped',
    };
  }

  if (slug.includes('ipc') || slug.includes('infection prevention')) {
    return {
      section: CPA_PROGRAMME,
      item: 'Infection Prevention & Control (IPC)',
      metric: 'performed',
      source: 'mapped',
    };
  }

  if (slug.includes('vhnd') || slug.includes('vhsnd') || slug.includes('village health')) {
    return {
      section: CPA_PROGRAMME,
      item: 'Village Health, Sanitation & Nutrition Day (VHSND/VHND)',
      metric: 'performed',
      source: 'mapped',
    };
  }

  return {
    section: sanitizeDisplayText(doc.corePracticeAreaTitle || doc.finalGroup || template.category || ''),
    item: sanitizeDisplayText(doc.templateTitle || template.title || template.name || ''),
    metric: 'performed',
    source: 'mapped',
  };
}

function decorateTemplate(template) {
  if (!template || typeof template !== 'object') return template;
  return {
    ...template,
    title: sanitizeDisplayText(template.title || template.name || ''),
    name: sanitizeDisplayText(template.name || template.title || ''),
    category: sanitizeDisplayText(template.category || ''),
    goals: sanitizeStringArray(template.goals || []),
    participationModes: templateParticipationModes(template),
    participationComponentOptions: templateParticipationComponentOptions(template),
    participationComponentsRequiredForModes: Array.isArray(template.participationComponentsRequiredForModes)
      ? template.participationComponentsRequiredForModes
          .map(normalizeParticipationMode)
          .filter(Boolean)
      : [],
    learningDomains: sanitizeStringArray(template.learningDomains || []),
    bloomLevels: sanitizeStringArray(template.bloomLevels || []),
    millerTarget: sanitizeDisplayText(template.millerTarget || ''),
    assessmentMethods: sanitizeStringArray(template.assessmentMethods || []),
    steps: Array.isArray(template.steps)
      ? template.steps.map((step) => ({
          ...step,
          title: sanitizeDisplayText(step.title || ''),
          description: sanitizeDisplayText(step.description || ''),
          section: sanitizeDisplayText(step.section || ''),
          label: sanitizeDisplayText(step.label || ''),
          visibleForModes: Array.isArray(step.visibleForModes)
            ? step.visibleForModes.map(normalizeParticipationMode).filter(Boolean)
            : [],
          requiredForModes: Array.isArray(step.requiredForModes)
            ? step.requiredForModes.map(normalizeParticipationMode).filter(Boolean)
            : [],
          fields: Array.isArray(step.fields)
            ? step.fields
                .map(normalizeStructuredField)
                .filter((field) => field.key && field.label)
            : [],
        }))
      : [],
  };
}

function decorateActivityDoc(item) {
  if (!item || typeof item !== 'object') return item;
  return {
    ...item,
    templateTitle: sanitizeDisplayText(item.templateTitle || ''),
    templateName: sanitizeDisplayText(item.templateName || ''),
    corePracticeAreaTitle: sanitizeDisplayText(item.corePracticeAreaTitle || item.practiceAreaTitle || ''),
    practiceAreaTitle: sanitizeDisplayText(item.practiceAreaTitle || item.corePracticeAreaTitle || ''),
    finalGroup: sanitizeDisplayText(item.finalGroup || ''),
    competencies: sanitizeStringArray(item.competencies || []),
    skills: sanitizeStringArray(item.skills || []),
    reportingTarget: item.reportingTarget && typeof item.reportingTarget === 'object'
      ? {
          ...item.reportingTarget,
          section: sanitizeDisplayText(item.reportingTarget.section || ''),
          item: sanitizeDisplayText(item.reportingTarget.item || ''),
          notes: sanitizeDisplayText(item.reportingTarget.notes || ''),
        }
      : {},
  };
}

// ✅ Minimal template fallback so old runs still render even if template fetch fails
function minimalTemplateFromDoc(item) {
  const title =
    item?.templateTitle ||
    item?.templateName ||
    item?.template?.title ||
    item?.template?.name ||
    'Guided Activity';

  const steps =
    Array.isArray(item?.template?.steps) ? item.template.steps :
    Array.isArray(item?.steps) ? item.steps :
    Array.isArray(item?.templateSteps) ? item.templateSteps :
    [];

  return decorateTemplate({ title, steps });
}

// Phase 1 structural alignment (no regression):
// - Keep this page as the focused guided-activity run / entry screen
// - Reposition it clearly under Activity Launcher rather than as a separate operational hub
// - Keep Reviewer Workbench as the main approval surface
// - Keep Comprehensive Services as the main citizen / OPD operational hub

export default function ActivityGuidedRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const provisional = id === 'new';
  const provisionalTemplateId = provisional ? new URLSearchParams(location.search).get('templateId') : null;
  const startPayloadRef = useRef(provisional
    ? readReloadSafeStartPayload(provisionalTemplateId, location.state?.guidedStartPayload)
    : null);
  const creationRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());
  const activityIdRef = useRef(provisional ? null : id);
  const routeIdRef = useRef(id);
  const loadGenerationRef = useRef(0);
  const skipCanonicalLoadRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const dirtyRef = useRef(false);
  const meaningfulInteractionRef = useRef(false);
  const revisionRef = useRef(0);
  const mountedRef = useRef(true);
  const payloadBuilderRef = useRef(null);
  const opdBlockedRef = useRef(false);
  const latestRef = useRef({ answers: [], counts: {} });
  const [activityId, setActivityId] = useState(provisional ? null : id);

  const activityLauncherPath = tenantUrl('/intern/activity/new');
  const activityHistoryPath = tenantUrl('/intern/activity/log');
  const reviewerWorkbenchPath = tenantUrl('/dashboard/approval');
  const operationsPath = tenantUrl('/dashboard/operations');

  const isIntern = useMemo(() => {
    const r = String(readUserRoleFromStorage() || '').toLowerCase();
    return r.includes('intern');
  }, []);

  const loggedInIdentity = useMemo(
    () => readLoggedInUserIdentityFromStorage(),
    []
  );

  const [doc, setDoc] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [counts, setCounts] = useState({
    consultations: 0,
    procedures: 0,
    immunizations: 0,
    observed: 0,
    performed: 0,
    commonAilments: 0,
    diagnosticTests: 0,
    entOphthal: 0,
  });
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showAdditionalCounts, setShowAdditionalCounts] = useState(false);
  const [validationIssues, setValidationIssues] = useState([]);
  const [clinicalWarning, setClinicalWarning] = useState('');
  const [reportedParticipationMode, setReportedParticipationMode] = useState('');
  const [participationComponents, setParticipationComponents] = useState([]);
  const [participationNote, setParticipationNote] = useState('');

  async function load(requestedId = id) {
    const generation = ++loadGenerationRef.current;
    const isCurrentLoad = () => generation === loadGenerationRef.current &&
      String(routeIdRef.current) === String(requestedId);
    try {
      setErr('');
      setOk('');
      setValidationIssues([]);
      setClinicalWarning('');

      // 1) Try existing wrapper first (keeps your current working behavior)
      let res;
      try {
        res = await activityApi.getActivity(requestedId);
      } catch (e) {
        if (!isCurrentLoad()) return;
        // 2) If wrapper hits wrong non-tenant path (common cause of intern 404), fall back
        const msg = String(e?.message || '');
        if (e?.status === 404 || /route not found/i.test(msg) || /not found/i.test(msg)) {
          res = await getActivityViaTenantEndpoints(requestedId);
        } else {
          throw e;
        }
      }

      if (!isCurrentLoad()) return;

      const item = decorateActivityDoc(res?.item || res?.activity || res?.data || res);
      setDoc(item);
      setReportedParticipationMode(
        normalizeParticipationMode(item?.reportedParticipationMode || item?.plannedParticipationMode || '')
      );
      setParticipationComponents(
        normalizeParticipationComponents(item?.participationComponents)
      );
      setParticipationNote(sanitizeDisplayText(item?.participationNote || ''));

      const c = item?.counts || {};
      const loadedCounts = {
        consultations: Number(c.consultations || 0),
        procedures: Number(c.procedures || 0),
        immunizations: Number(c.immunizations || 0),
        observed: Number(c.observed || 0),
        performed: Number(c.performed || 0),
        commonAilments: Number(c.commonAilments || 0),
        diagnosticTests: Number(c.diagnosticTests || 0),
        entOphthal: Number(c.entOphthal || 0),
      };
      setCounts(loadedCounts);
      setShowAdditionalCounts(hasRecordedTopLevelCounts(loadedCounts));

      // ✅ Template fetch with fallback
      const templateId =
        item?.templateId ||
        item?.template?._id ||
        item?.template?.id ||
        item?.templateRef ||
        item?.templateID;

      let tplItem = null;
      if (templateId) {
        try {
          const t = await activityApi.getTemplate(templateId);
          if (!isCurrentLoad()) return;
          tplItem = decorateTemplate(t?.item || t?.data || t);
        } catch (_e) {
          if (!isCurrentLoad()) return;
          tplItem = decorateTemplate(minimalTemplateFromDoc(item));
        }
      } else {
        tplItem = decorateTemplate(minimalTemplateFromDoc(item));
      }

      if (!isCurrentLoad()) return;
      setTpl(tplItem);

      const steps = (tplItem?.steps || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const fromDoc = Array.isArray(item?.stepData) ? item.stepData : [];
      const editableRunStatus = [
        'draft',
        'returned',
        'observed-returned',
        'repeat',
        'observed-repeat',
      ].includes(String(item?.status || '').toLowerCase());

      const merged = steps.map((s) => {
        const hit = fromDoc.find((x) => x.order === s.order) || {};
        const decoded = decodeStructured(tplItem, s.order, hit.valueText || '');
        return {
          order: s.order,
          key: hit.key || s.key || '',
          label: sanitizeDisplayText(hit.label || s.label || s.title || ''),
          section: sanitizeDisplayText(hit.section || s.section || ''),
          valueText: decoded.notes || '',
          valueCount:
            hit.valueCount !== undefined && hit.valueCount !== null
              ? Number(hit.valueCount)
              : (editableRunStatus ? fixedNumericStepValue(s) : undefined),
          valueBool: hit.valueBool,
          photos: hit.photos || [],
          structured: decoded.structured || {},
        };
      });

      latestRef.current = { answers: merged, counts: loadedCounts };
      setAnswers(merged);
    } catch (e) {
      if (isCurrentLoad()) setErr(e?.message || 'Failed to load activity');
    }
  }

  async function loadProvisional() {
    try {
      setErr('');
      setValidationIssues([]);
      const templateId = new URLSearchParams(location.search).get('templateId');
      if (!templateId) throw new Error('Activity template is missing. Return to Activity Launcher and choose an activity.');
      const response = await activityApi.getTemplate(templateId);
      const template = decorateTemplate(response?.item || response?.data || response);
      const context = { ...(startPayloadRef.current || {}), ...template };
      if (isOpdHandoffTemplate(context)) {
        opdBlockedRef.current = true;
        setTpl(template);
        setDoc({ status: 'opd-handoff', ...context, templateId });
        setErr('OPD / Primary Care is available through Comprehensive Services and cannot be started as a guided activity.');
        return;
      }
      setTpl(template);
      setDoc({
        status: 'draft',
        ...(startPayloadRef.current || {}),
        templateId,
        templateTitle: template?.title || '',
      });
      setReportedParticipationMode(
        normalizeParticipationMode(
          startPayloadRef.current?.reportedParticipationMode ||
          startPayloadRef.current?.plannedParticipationMode ||
          ''
        )
      );
      setParticipationComponents(
        normalizeParticipationComponents(
          startPayloadRef.current?.participationComponents
        )
      );
      setParticipationNote(sanitizeDisplayText(startPayloadRef.current?.participationNote || ''));
      if (!startPayloadRef.current?.centre) {
        setErr('Centre context could not be restored. Return to Activity Launcher before entering evidence. No activity has been created.');
      }
      const initialAnswers = (template?.steps || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((step) => ({
        order: step.order,
        key: step.key || '',
        label: sanitizeDisplayText(step.label || step.title || ''),
        section: sanitizeDisplayText(step.section || ''),
        valueText: '',
        valueCount: fixedNumericStepValue(step),
        valueBool: undefined,
        photos: [],
        structured: {},
      }));
      latestRef.current = { answers: initialAnswers, counts: latestRef.current.counts };
      setAnswers(initialAnswers);
      setShowAdditionalCounts(false);
    } catch (e) {
      setErr(e?.message || 'Failed to load activity template');
    }
  }

  useEffect(() => {
    if (skipCanonicalLoadRef.current) {
      skipCanonicalLoadRef.current = false;
      routeIdRef.current = id;
      return;
    }

    const previousRouteId = routeIdRef.current;
    const changingRealActivity = previousRouteId && previousRouteId !== 'new' &&
      id !== 'new' && String(previousRouteId) !== String(id);

    if (changingRealActivity) {
      clearTimeout(autosaveTimerRef.current);
      const oldRevision = revisionRef.current;
      if (dirtyRef.current && payloadBuilderRef.current) {
        void queueSave(
          String(previousRouteId),
          payloadBuilderRef.current(),
          oldRevision,
          { reportError: false }
        ).catch(() => {});
      }

      // Detach all mutable run state from the old route before loading the new
      // document. Incrementing the revision also prevents an old queued save
      // from marking later edits to the new run as clean.
      revisionRef.current += 1;
      dirtyRef.current = false;
      meaningfulInteractionRef.current = false;
      creationRef.current = null;
      opdBlockedRef.current = false;
      payloadBuilderRef.current = null;
      latestRef.current = { answers: [], counts: {} };
      activityIdRef.current = id;
      setActivityId(id);
      setDoc(null);
      setTpl(null);
      setAnswers([]);
      setShowAdditionalCounts(false);
      setValidationIssues([]);
      setReportedParticipationMode('');
      setParticipationComponents([]);
      setParticipationNote('');
    }

    routeIdRef.current = id;
    if (provisional) loadProvisional();
    else load(id);
    // eslint-disable-next-line
  }, [id]);

  const byOrder = useMemo(
    () => (tpl?.steps || [])
      .filter((step) => isStepVisibleForMode(step, reportedParticipationMode))
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [tpl, reportedParticipationMode]
  );

  function isMeaningfulPatch(patch) {
    if (Object.prototype.hasOwnProperty.call(patch, 'valueBool')) return true;
    if (Object.prototype.hasOwnProperty.call(patch, 'valueCount')) return true;
    if (String(patch.valueText || '').trim()) return true;
    if ((patch.photos || []).some(Boolean)) return true;
    return patch.structured && Object.values(patch.structured).some((value) => String(value ?? '').trim() !== '');
  }

  function updAns(order, patch) {
    const current = latestRef.current.answers;
    const next = current.map((it) => (it.order === order ? { ...it, ...patch } : it));
    setAnswers(next);
    setValidationIssues((issues) => issues.filter((issue) => Number(issue.order) !== Number(order)));
    dirtyRef.current = true;
    revisionRef.current += 1;
    latestRef.current = { answers: next, counts: latestRef.current.counts };
    payloadBuilderRef.current = () => activityPayload(next, latestRef.current.counts);
    if (isMeaningfulPatch(patch)) {
      meaningfulInteractionRef.current = true;
      void ensureActivity(next, latestRef.current.counts).catch(() => {});
    }
  }

  function updStructuredAns(order, currentStructured, fieldKey, value) {
    const patch = {
      structured: {
        ...(currentStructured || {}),
        [fieldKey]: value,
      },
    };
    const current = latestRef.current.answers;
    const next = current.map((item) =>
      item.order === order ? { ...item, ...patch } : item
    );
    const consistencyIssues = validateImnciClinicalConsistency(tpl, next);
    const conflict = consistencyIssues
      .find((issue) => String(issue.fieldKey || '') === String(fieldKey || ''));

    const warningMessage = (issue) => {
      if (!issue) return '';
      const title = sanitizeDisplayText(issue.title || issue.fieldKey || '');
      return `Clinical consistency warning${title ? ` — ${title}` : ''}: ${issue.reason}`;
    };

    if (conflict) {
      // Do not persist a contradictory downstream IMNCI decision. Recording the
      // urgent/severe finding itself remains allowed; only the conflicting
      // escalation/severity/referral answer is rejected.
      setClinicalWarning(warningMessage(conflict));
      return;
    }

    // If a clinical finding was just changed and it makes an already-recorded
    // downstream decision inconsistent, keep the finding but surface the first
    // remaining contradiction. Clear the warning once the state is coherent.
    setClinicalWarning(warningMessage(consistencyIssues[0]));

    updAns(order, patch);
  }

  function updCount(key, value) {
    const next = { ...latestRef.current.counts, [key]: Number(value || 0) };
    setCounts(next);
    dirtyRef.current = true;
    revisionRef.current += 1;
    latestRef.current = { answers: latestRef.current.answers, counts: next };
    payloadBuilderRef.current = () => activityPayload(latestRef.current.answers, next);
    meaningfulInteractionRef.current = true;
    void ensureActivity(latestRef.current.answers, next).catch(() => {});
  }

  function updateParticipationMode(value) {
    const next = normalizeParticipationMode(value);
    const allowedComponentValues = new Set(
      templateParticipationComponentOptions(tpl, next).map((item) => item.value)
    );
    const filteredComponents = normalizeParticipationComponents(participationComponents)
      .filter((component) => allowedComponentValues.has(component));

    setReportedParticipationMode(next);
    setParticipationComponents(filteredComponents);
    setValidationIssues([]);
    dirtyRef.current = true;
    revisionRef.current += 1;
    meaningfulInteractionRef.current = true;
    void ensureActivity(latestRef.current.answers, latestRef.current.counts).catch(() => {});
  }

  function updateParticipationComponents(values) {
    const next = normalizeParticipationComponents(values);
    setParticipationComponents(next);
    setValidationIssues([]);
    dirtyRef.current = true;
    revisionRef.current += 1;
    meaningfulInteractionRef.current = true;
    void ensureActivity(latestRef.current.answers, latestRef.current.counts).catch(() => {});
  }

  function updateParticipationNote(value) {
    setParticipationNote(value);
    dirtyRef.current = true;
    revisionRef.current += 1;
    meaningfulInteractionRef.current = true;
    void ensureActivity(latestRef.current.answers, latestRef.current.counts).catch(() => {});
  }

  function activityPayload(currentAnswers = answers, currentCounts = counts) {
    const reportingTarget = buildGuidedReportingTarget({ template: tpl, doc });
    return {
      stepData: buildStepPayload(currentAnswers),
      counts: currentCounts,
      templateSlug: tpl?.slug || doc?.templateSlug || '',
      templateTitle: tpl?.title || doc?.templateTitle || '',
      activityKey: doc?.activityKey || tpl?.slug || '',
      sourceFlow: 'guided',
      corePracticeAreaTitle: reportingTarget.section || doc?.corePracticeAreaTitle || '',
      practiceAreaTitle: reportingTarget.section || doc?.practiceAreaTitle || '',
      finalGroup: reportingTarget.section || doc?.finalGroup || '',
      competencies: doc?.competencies || [], skills: doc?.skills || [], reportingTarget,
      plannedParticipationMode: normalizeParticipationMode(
        doc?.plannedParticipationMode || startPayloadRef.current?.plannedParticipationMode || ''
      ),
      reportedParticipationMode: normalizeParticipationMode(reportedParticipationMode),
      participationNote: sanitizeDisplayText(participationNote || ''),
      participationComponents: normalizeParticipationComponents(participationComponents),
      directSupervisorIds: Array.isArray(doc?.directSupervisorIds) ? doc.directSupervisorIds : [],
      directObserverIds: Array.isArray(doc?.directObserverIds) ? doc.directObserverIds : [],
    };
  }

  function queueSave(targetId, payload, revision, { reportError = true } = {}) {
    const run = () => activityApi.saveActivity(targetId, payload).then((result) => {
      if (revisionRef.current === revision) dirtyRef.current = false;
      return result;
    });
    const queued = saveQueueRef.current.catch(() => {}).then(run);
    saveQueueRef.current = queued;
    if (reportError) {
      queued.catch((error) => {
        if (mountedRef.current) setErr(error?.message || 'Failed to save');
      });
    }
    return queued;
  }

  async function ensureActivity(currentAnswers = answers, currentCounts = counts) {
    if (opdBlockedRef.current) throw new Error('OPD / Primary Care must be opened through Comprehensive Services.');
    if (!startPayloadRef.current?.centre) throw new Error('Centre context is required. Return to Activity Launcher and reopen this guided activity.');
    if (activityIdRef.current) return activityIdRef.current;
    if (creationRef.current) return creationRef.current;
    creationRef.current = (async () => {
      try {
        const startContext = { ...(startPayloadRef.current || {}) };
        delete startContext.__guidedCentreContext;
        const res = await activityApi.startActivity({ ...startContext, ...activityPayload(currentAnswers, currentCounts) });
        const created = res?.item || res?.activity || res?.data || res;
        const newId = created?._id || created?.id || created?.activityId || created?.activityInstanceId;
        if (!newId) throw new Error('Activity ID missing in response');
        const realId = String(newId);
        activityIdRef.current = realId;
        setActivityId(realId);
        setDoc((current) => ({ ...current, ...created, _id: newId, status: created?.status || 'draft' }));
        skipCanonicalLoadRef.current = true;
        navigate(tenantUrl(`/intern/activity/run/${encodeURIComponent(realId)}`), { replace: true });
        try { sessionStorage.removeItem(provisionalContextKey(doc?.templateId || provisionalTemplateId)); } catch {}
        const latest = latestRef.current;
        const initialRevision = revisionRef.current;
        try {
          await queueSave(realId, activityPayload(latest.answers, latest.counts), initialRevision, { reportError: false });
        } catch (saveError) {
          if (mountedRef.current) setErr(saveError?.message || 'Activity created, but evidence could not be saved. Your changes will be retried against this draft.');
        }
        return realId;
      } catch (startError) {
        if (activityIdRef.current) {
          if (mountedRef.current) setErr(startError?.message || 'Activity created, but its first save could not be completed.');
          return activityIdRef.current;
        }
        creationRef.current = null;
        if (mountedRef.current) setErr(startError?.message || 'Could not start activity');
        throw startError;
      }
    })();
    return creationRef.current;
  }

  useEffect(() => {
    latestRef.current = { answers, counts };
    payloadBuilderRef.current = () => activityPayload(latestRef.current.answers, latestRef.current.counts);
    if (!activityId || !tpl || !dirtyRef.current) return undefined;
    clearTimeout(autosaveTimerRef.current);
    const saveRevision = revisionRef.current;
    autosaveTimerRef.current = setTimeout(() => {
      const latest = latestRef.current;
      void queueSave(activityId, activityPayload(latest.answers, latest.counts), saveRevision);
    }, 700);
    return () => clearTimeout(autosaveTimerRef.current);
    // activityPayload intentionally reads the latest template/document context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, counts, activityId, tpl, reportedParticipationMode, participationComponents, participationNote]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(autosaveTimerRef.current);
      const realId = activityIdRef.current;
      if (realId && dirtyRef.current && payloadBuilderRef.current) {
        void queueSave(realId, payloadBuilderRef.current(), revisionRef.current, { reportError: false }).catch(() => {});
      }
    };
  }, []);

  const status = String(doc?.status || '').toLowerCase();
  const readOnly = !['draft', 'returned', 'observed-returned', 'repeat', 'observed-repeat'].includes(status);

  function buildStepPayload(sourceAnswers = answers) {
    if (!tpl) {
      return sourceAnswers.map((a) => ({
        order: a.order,
        key: a.key || '',
        label: sanitizeDisplayText(a.label || ''),
        section: sanitizeDisplayText(a.section || ''),
        valueText: a.valueText || '',
        valueCount: a.valueCount,
        valueBool: a.valueBool,
        photos: a.photos || [],
      }));
    }

    return sourceAnswers.map((a) => {
      const step = (Array.isArray(tpl?.steps) ? tpl.steps : [])
        .find((item) => Number(item?.order) === Number(a.order));
      const templateDrivenFields = Array.isArray(step?.fields) ? step.fields : [];
      let structuredForSave = a.structured || {};

      if (templateDrivenFields.length > 0) {
        const visibleKeys = new Set(
          getStructuredFields(tpl, a.order)
            .filter((field) =>
              isStructuredFieldVisible(
                field,
                a.order,
                reportedParticipationMode,
                sourceAnswers
              )
            )
            .map((field) => field.key)
        );
        structuredForSave = Object.fromEntries(
          Object.entries(a.structured || {})
            .filter(([key]) => visibleKeys.has(key))
        );
      }

      const valueText = encodeStructured(
        tpl,
        a.order,
        structuredForSave,
        a.valueText
      );
      return {
        order: a.order,
        key: a.key || '',
        label: sanitizeDisplayText(a.label || ''),
        section: sanitizeDisplayText(a.section || ''),
        valueText,
        valueCount: a.valueCount,
        valueBool: a.valueBool,
        photos: a.photos || [],
      };
    });
  }

  async function save(opts = { silent: false }) {
    try {
      if (!tpl) return;
      if (!opts.silent) {
        setErr('');
        setOk('');
      }
      if (!activityIdRef.current && !meaningfulInteractionRef.current) {
        throw new Error('Enter activity evidence before saving or submitting this guided activity.');
      }
      const targetId = activityId || await ensureActivity();
      const latest = latestRef.current;
      await queueSave(targetId, activityPayload(latest.answers, latest.counts), revisionRef.current, { reportError: false });
      if (!opts.silent) {
        setOk('Saved ✓');
      }
      return targetId;
    } catch (e) {
      setErr(e?.message || 'Failed to save');
      throw e; // rethrow so submit() can react if needed
    }
  }

  async function submit() {
    if (readOnly || submitting) return;

    if (isRoleAwareTemplate(tpl) && !normalizeParticipationMode(reportedParticipationMode)) {
      setOk('');
      setErr('Select what you actually did in this activity before submitting.');
      return;
    }

    if (
      participationComponentsRequiredForMode(tpl, reportedParticipationMode) &&
      normalizeParticipationComponents(participationComponents).length === 0
    ) {
      setOk('');
      setErr('Select the component(s) or scope that you actually participated in before submitting.');
      return;
    }

    const localIssues = validateRequiredActivityEvidence(
      tpl,
      latestRef.current.answers,
      reportedParticipationMode,
      participationComponents
    );
    if (localIssues.length) {
      setOk('');
      setErr('');
      setValidationIssues(localIssues);
      return;
    }

    setValidationIssues([]);

    if (!activityIdRef.current && !meaningfulInteractionRef.current) {
      setOk('');
      setErr('Enter activity evidence before submitting this guided activity.');
      return;
    }

    try {
      setSubmitting(true);
      setErr('');
      setOk('');

      // Ensure latest answers are saved before submission.
      // Draft save/autosave remains intentionally permissive.
      const submitId = await save({ silent: true });

      // ✅ Submit via backend-safe tenant endpoints first (prevents localhost:3000 404)
      try {
        await submitViaTenantEndpoints(submitId);
      } catch (e) {
        // Only endpoint-compatibility exhaustion may use the existing API fallback.
        // Validation/auth/business errors must surface directly and must not probe a
        // legacy submission path that could obscure the real backend response.
        if (e?.endpointFallbackEligible) {
          await activityApi.submitForObservation(submitId);
        } else {
          throw e;
        }
      }

      setValidationIssues([]);

      // On success: go back to Start Activity page (Interns submit for review; non-Interns finalize directly).
      setOk(isIntern ? 'Submitted for review ✓' : 'Finalized ✓');
      navigate(activityLauncherPath);
    } catch (e) {
      const backendIssues = Array.isArray(e?.missingSteps) ? e.missingSteps : [];
      if (e?.code === 'ACTIVITY_EVIDENCE_INCOMPLETE') {
        setValidationIssues(backendIssues);
        setErr(backendIssues.length
          ? validationSummary(backendIssues)
          : (e?.message || 'Complete the required activity evidence before submitting.'));
      } else if (e?.code === 'ACTIVITY_PARTICIPATION_REQUIRED') {
        setErr(e?.message || 'Select what you actually did before submitting.');
      } else if (e?.code === 'ACTIVITY_PARTICIPATION_NOT_ALLOWED') {
        setErr(e?.message || 'That participation level is not allowed for this activity.');
      } else if (e?.status === 401) {
        setErr('Session expired or not authorised. Please log in again from CMFM portal.');
      } else {
        setErr(e?.message || 'Failed to submit');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ✅ If load failed, show error instead of hanging at Loading...
  if ((!doc || !tpl) && err) {
    return (
      <div className="p-4">
        <Alert severity="error">{err}</Alert>
      </div>
    );
  }

  if (!doc || !tpl) return <div className="p-4">Loading…</div>;

  const hasSavedAdditionalCounts = hasRecordedTopLevelCounts(counts);

  const observerName = firstPersonDisplayName(
    doc?.observerName,
    doc?.observer,
    doc?.reviewerName,
    doc?.reviewer
  );
  const approverName = firstPersonDisplayName(
    doc?.approverName,
    doc?.approvedBy,
    doc?.signoffBy
  );
  const ownerIdentity = activityOwnerIdentity(doc, loggedInIdentity);

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-xl font-semibold">Guided Activity Run</h1>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Use this page only to enter or continue a guided activity run. Activity Launcher remains the main guided-activity start surface, Reviewer Workbench remains the main approval surface, and Comprehensive Services remains the main citizen / OPD operational hub.
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
            {sanitizeDisplayText(tpl.title)}
          </Typography>
        </div>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button
            component={Link}
            to={activityLauncherPath}
            size="small"
            variant="outlined"
          >
            Activity Launcher
          </Button>
          <Button
            component={Link}
            to={activityHistoryPath}
            size="small"
            variant="outlined"
          >
            Activity History
          </Button>
          {!isIntern && (
            <Button
              component={Link}
              to={reviewerWorkbenchPath}
              size="small"
              variant="outlined"
            >
              Reviewer Workbench
            </Button>
          )}
          <Button
            component={Link}
            to={operationsPath}
            size="small"
            variant="outlined"
          >
            Comprehensive Services
          </Button>
        </Stack>
      </div>

      {err && <Alert severity="error">{err}</Alert>}
      {clinicalWarning && <Alert severity="warning">{clinicalWarning}</Alert>}
      {ok && <Alert severity="success">{ok}</Alert>}
      {!err && validationIssues.length > 0 && (
        <Alert severity="error">{validationSummary(validationIssues)}</Alert>
      )}

      {/* Status header */}
      <Box className="text-sm opacity-80">
        {doc?.date ? new Date(doc.date).toDateString() : '—'} • Centre:{' '}
        <b>{doc?.centre || startPayloadRef.current?.centre || 'No current posting assigned'}</b>
        {doc?.shift ? ` • ${doc.shift}` : ''} • Status:{' '}
        <b className="uppercase">{String(doc?.status || 'new').toUpperCase()}</b>
      </Box>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          Activity record owner
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.5, sm: 2 }}
          sx={{ flexWrap: 'wrap', alignItems: { sm: 'center' } }}
        >
          <Typography variant="body2">
            <strong>Name:</strong> {ownerIdentity.name || '—'}
          </Typography>
          <Typography variant="body2">
            <strong>Roll / registration:</strong> {ownerIdentity.rollNo || '—'}
          </Typography>
          {ownerIdentity.role && (
            <Typography variant="body2">
              <strong>Role:</strong> {ownerIdentity.role}
            </Typography>
          )}
          {ownerIdentity.userId && (
            <Typography variant="caption" sx={{ opacity: 0.72 }}>
              Account ID: {ownerIdentity.userId}
            </Typography>
          )}
        </Stack>
      </Box>

      {(observerName || approverName) && (
        <Box className="flex flex-wrap items-center gap-2">
          {observerName && <Chip size="small" label={`Observer: ${observerName}`} />}
          {approverName && (
            <Chip size="small" color="success" label={`Approver: ${approverName}`} />
          )}
        </Box>
      )}

      {readOnly && (
        <Alert severity={(status === 'approved' || status === 'observed-approved') ? 'success' : 'info'}>
          {(status === 'approved' || status === 'observed-approved')
            ? 'This run is approved. It will appear automatically in Activity Log and the rotation report after sync.'
            : 'This run is submitted/read-only. Once approved, it will appear automatically in Activity Log and the rotation report.'}
        </Alert>
      )}

      {isRoleAwareTemplate(tpl) && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            Your participation in this activity
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.78, mb: 1.5 }}>
            {isIntern
              ? 'Record what actually happened. A planned/nominated role is only an expectation; the observer/reviewer confirms the final participation level.'
              : 'Record what actually happened. A planned/nominated role is only an expectation; your submitted participation level is finalized with this activity and does not require a separate reviewer decision.'}
          </Typography>

          {doc?.plannedParticipationMode && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Planned / nominated capacity: <strong>{participationModeLabel(doc.plannedParticipationMode)}</strong>
            </Alert>
          )}

          <TextField
            select
            fullWidth
            size="small"
            label="What did you actually do?"
            value={reportedParticipationMode}
            onChange={(e) => updateParticipationMode(e.target.value)}
            disabled={readOnly}
            required
            helperText="Choose the level that best matches your actual participation, not the activity title."
          >
            {templateParticipationModes(tpl).map((mode) => (
              <MenuItem key={mode.key} value={mode.key}>
                {mode.label || participationModeLabel(mode.key)}
              </MenuItem>
            ))}
          </TextField>

          {normalizeParticipationMode(reportedParticipationMode) && (
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.78 }}>
              {templateParticipationModes(tpl).find((mode) => mode.key === normalizeParticipationMode(reportedParticipationMode))?.description || ''}
            </Typography>
          )}

          {templateParticipationComponentOptions(tpl, reportedParticipationMode).length > 0 && (
            <TextField
              select
              fullWidth
              size="small"
              label="Scope of your participation / assigned component(s)"
              value={normalizeParticipationComponents(participationComponents)}
              onChange={(e) => {
                const selected = Array.isArray(e.target.value)
                  ? e.target.value
                  : String(e.target.value || '').split(',');
                updateParticipationComponents(selected);
              }}
              disabled={readOnly || !normalizeParticipationMode(reportedParticipationMode)}
              required={participationComponentsRequiredForMode(tpl, reportedParticipationMode)}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  const values = Array.isArray(selected) ? selected : [];
                  const options = templateParticipationComponentOptions(
                    tpl,
                    reportedParticipationMode
                  );
                  return values
                    .map((value) => options.find((item) => item.value === value)?.label || value)
                    .join(', ');
                },
              }}
              helperText={
                normalizeParticipationMode(reportedParticipationMode) === 'performed_independent'
                  ? 'Independent assigned task means one or more defined components within the learner role; it does not mean independent management of the whole sick child.'
                  : 'Choose the part(s) of the encounter you actually observed, assisted with or performed.'
              }
              sx={{ mt: 1.5 }}
            >
              {templateParticipationComponentOptions(tpl, reportedParticipationMode).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    {option.description && (
                      <Typography variant="caption" sx={{ opacity: 0.68 }}>
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Participation note (optional)"
            value={participationNote}
            onChange={(e) => updateParticipationNote(e.target.value)}
            disabled={readOnly}
            sx={{ mt: 1.5 }}
            placeholder="Example: Assisted with delivery preparation and immediate newborn care; principal delivery was conducted by JR."
          />

          {(Array.isArray(doc?.directSupervisorIds) && doc.directSupervisorIds.length > 0) && (
            <Chip size="small" sx={{ mt: 1.5 }} label={`Supervisor linked: ${doc.directSupervisorIds.length}`} />
          )}
        </Box>
      )}

      <Divider />

      {/* Optional top-level totals.
          These legacy/general counters remain available for compatibility, but
          they are no longer shown as if every guided activity requires all five.
          Template step evidence is the primary capture surface. */}
      <Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
        >
          <div>
            <Typography variant="subtitle2">Additional activity totals</Typography>
            <Typography variant="body2" sx={{ opacity: 0.72 }}>
              Optional compatibility totals. Use the guided steps below as the primary evidence record.
            </Typography>
            {!showAdditionalCounts && hasSavedAdditionalCounts && (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Saved totals are present for this run.
              </Typography>
            )}
          </div>
          <Button
            size="small"
            variant="text"
            onClick={() => setShowAdditionalCounts((current) => !current)}
          >
            {showAdditionalCounts ? 'Hide totals' : 'Show totals'}
          </Button>
        </Stack>

        {showAdditionalCounts && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-3">
            <TextField
              label="Consultations"
              type="number"
              value={counts.consultations}
              onChange={(e) =>
                updCount('consultations', e.target.value)
              }
              disabled={readOnly}
            />
            <TextField
              label="Procedures"
              type="number"
              value={counts.procedures}
              onChange={(e) =>
                updCount('procedures', e.target.value)
              }
              disabled={readOnly}
            />
            <TextField
              label="Immunizations"
              type="number"
              value={counts.immunizations}
              onChange={(e) =>
                updCount('immunizations', e.target.value)
              }
              disabled={readOnly}
            />
            <TextField
              label="Observed"
              type="number"
              value={counts.observed}
              onChange={(e) =>
                updCount('observed', e.target.value)
              }
              disabled={readOnly}
              helperText="Training exposure"
            />
            <TextField
              label="Performed"
              type="number"
              value={counts.performed}
              onChange={(e) =>
                updCount('performed', e.target.value)
              }
              disabled={readOnly}
              helperText="Training evidence"
            />
          </div>
        )}
      </Box>

      {/* Steps */}
      <div className="space-y-4">
        {byOrder.map((s) => {
          const a = answers.find((x) => x.order === s.order) || {};
          const fieldDefs = getStructuredFields(tpl, s.order);
          const structured = a.structured || {};
          const visibleFieldDefs = fieldDefs.filter((field) =>
            isStructuredFieldVisible(
              field,
              s.order,
              reportedParticipationMode,
              answers
            )
          );
          // A conditional clinical module may have an applicability controller
          // that is itself conditional on the age/pathway (for example the cough,
          // fever and ear controllers in the older-child IMNCI pathway). Treat
          // those controller fields separately from the evidence fields they
          // activate, otherwise a visible-but-unanswered controller makes the
          // module look active and leaves a misleading empty Notes box visible.
          const sameStepControllerKeys = new Set(
            fieldDefs
              .map((field) => field?.showWhen)
              .filter((condition) =>
                condition?.fieldKey &&
                (
                  !condition.stepOrder ||
                  Number(condition.stepOrder) === Number(s.order)
                )
              )
              .map((condition) => sanitizeDisplayText(condition.fieldKey))
              .filter(Boolean)
          );
          const controllerFieldDefs = fieldDefs.filter((field) =>
            sameStepControllerKeys.has(field.key)
          );
          const visibleControllerFieldDefs = visibleFieldDefs.filter((field) =>
            sameStepControllerKeys.has(field.key)
          );
          const visibleBranchEvidenceDefs = visibleFieldDefs.filter((field) =>
            !sameStepControllerKeys.has(field.key)
          );
          const controllerHasValue = visibleControllerFieldDefs.some((field) =>
            hasMeaningfulEvidenceValue(structured[field.key])
          );

          // Preserve core/required steps exactly as before. The compact inactive
          // state is only for optional template-driven branches such as cough,
          // diarrhoea, fever, ear and young-infant modules.
          const structuredBranchInactive =
            s.required === false &&
            fieldDefs.length > 0 &&
            controllerFieldDefs.length > 0 &&
            (
              visibleControllerFieldDefs.length === 0 ||
              visibleBranchEvidenceDefs.length === 0
            );
          const hasExistingNotes = String(a.valueText || '').trim().length > 0;
          const showSupplementalNotes =
            fieldDefs.length === 0 ||
            !structuredBranchInactive ||
            hasExistingNotes;
          const inactiveStructuredMessage =
            visibleControllerFieldDefs.length === 0 || controllerHasValue
              ? 'Not active for the selected pathway/applicability.'
              : 'Select the pathway/applicability above to activate this module.';
          const evidenceType = normalizeEvidenceTypeForRender(s.evidenceType);
          const fixedNumericValue = fixedNumericStepValue(s);
          const stepOptions = normalizeStepOptions(s);
          const helperText = stepHelperText(s);
          const placeholder = sanitizeDisplayText(s.placeholder || '');
          const stepIssue = validationIssues.find((issue) => Number(issue.order) === Number(s.order));
          const stepRequired = isStepRequiredForMode(s, reportedParticipationMode);

          return (
            <div
              key={s.order}
              className="p-3 border rounded-md dark:border-neutral-700"
              style={stepIssue ? { borderColor: '#d32f2f', borderWidth: 2 } : undefined}
            >
              <div className="font-medium">
                {s.order}. {sanitizeDisplayText(s.title)}
              </div>
              {s.description && (
                <div className="text-sm opacity-80 mb-2">{sanitizeDisplayText(s.description)}</div>
              )}

              {visibleFieldDefs.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  {visibleFieldDefs
                    .map((f) => {
                      const value = structured[f.key];
                      const fieldRequired = isStructuredFieldRequired(
                        f,
                        reportedParticipationMode,
                        participationComponents
                      );
                      const fieldIssue = validationIssues.find(
                        (issue) =>
                          Number(issue.order) === Number(s.order) &&
                          String(issue.fieldKey || '') === String(f.key)
                      );
                      const helperParts = [
                        sanitizeDisplayText(f.helpText || ''),
                        f.unit ? `Unit: ${sanitizeDisplayText(f.unit)}` : '',
                        fieldIssue?.reason || '',
                      ].filter(Boolean);
                      const helper = helperParts.join(' • ');
                      const baseSx = fieldIssue
                        ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f' } }
                        : undefined;

                      if (f.type === 'select' && f.options.length > 0) {
                        return (
                          <TextField
                            key={f.key}
                            select
                            label={f.label}
                            value={value ?? ''}
                            onChange={(e) =>
                              updStructuredAns(s.order, structured, f.key, e.target.value)
                            }
                            fullWidth
                            required={fieldRequired}
                            disabled={readOnly}
                            helperText={helper || undefined}
                            error={!!fieldIssue}
                            sx={baseSx}
                          >
                            <MenuItem value="">
                              <em>Select</em>
                            </MenuItem>
                            {f.options.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        );
                      }

                      if (f.type === 'multiselect' && f.options.length > 0) {
                        const selectedValues = Array.isArray(value)
                          ? value.map((item) => String(item))
                          : parseMultiselectValue(value);
                        return (
                          <TextField
                            key={f.key}
                            select
                            label={f.label}
                            value={selectedValues}
                            onChange={(e) => {
                              const selected = Array.isArray(e.target.value)
                                ? e.target.value
                                : String(e.target.value || '').split(',');
                              updStructuredAns(
                                s.order,
                                structured,
                                f.key,
                                [...new Set(
                                  selected
                                    .map((item) => String(item || '').trim())
                                    .filter(Boolean)
                                )]
                              );
                            }}
                            fullWidth
                            required={fieldRequired}
                            disabled={readOnly}
                            error={!!fieldIssue}
                            helperText={helper || undefined}
                            sx={baseSx}
                            SelectProps={{
                              multiple: true,
                              renderValue: (selected) => {
                                const selectedItems = Array.isArray(selected) ? selected : [];
                                return selectedItems
                                  .map((selectedValue) =>
                                    f.options.find(
                                      (option) => option.value === String(selectedValue)
                                    )?.label || String(selectedValue)
                                  )
                                  .join(', ');
                              },
                            }}
                          >
                            {f.options.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        );
                      }

                      if (f.type === 'boolean') {
                        return (
                          <TextField
                            key={f.key}
                            select
                            label={f.label}
                            value={typeof value === 'boolean' ? String(value) : ''}
                            onChange={(e) => {
                              const raw = e.target.value;
                              updStructuredAns(
                                s.order,
                                structured,
                                f.key,
                                raw === '' ? undefined : raw === 'true'
                              );
                            }}
                            fullWidth
                            required={fieldRequired}
                            disabled={readOnly}
                            error={!!fieldIssue}
                            helperText={helper || undefined}
                            sx={baseSx}
                          >
                            <MenuItem value="">
                              <em>Select</em>
                            </MenuItem>
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </TextField>
                        );
                      }

                      if (f.type === 'checkbox') {
                        return (
                          <Box key={f.key} sx={{ py: 1 }}>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={value === true}
                                onChange={(e) =>
                                  updStructuredAns(
                                    s.order,
                                    structured,
                                    f.key,
                                    !!e.target.checked
                                  )
                                }
                                disabled={readOnly}
                              />
                              <span>
                                {f.label}
                                {fieldRequired ? ' *' : ''}
                              </span>
                            </label>
                            {helper && (
                              <Typography
                                variant="caption"
                                color={fieldIssue ? 'error' : 'text.secondary'}
                                sx={{ display: 'block', mt: 0.5 }}
                              >
                                {helper}
                              </Typography>
                            )}
                          </Box>
                        );
                      }

                      const isDate = f.type === 'date';
                      const isNumber = f.type === 'number';
                      const isTextarea = f.type === 'textarea';

                      return (
                        <TextField
                          key={f.key}
                          label={f.label}
                          type={isDate ? 'date' : isNumber ? 'number' : 'text'}
                          value={value ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value;
                            updStructuredAns(
                              s.order,
                              structured,
                              f.key,
                              isNumber
                                ? (raw === '' ? undefined : Number(raw))
                                : raw
                            );
                          }}
                          fullWidth
                          multiline={isTextarea}
                          minRows={isTextarea ? 2 : undefined}
                          InputLabelProps={isDate ? { shrink: true } : undefined}
                          inputProps={
                            isNumber
                              ? {
                                  ...(f.min !== undefined && f.min !== null
                                    ? { min: f.min }
                                    : {}),
                                  ...(f.max !== undefined && f.max !== null
                                    ? { max: f.max }
                                    : {}),
                                }
                              : undefined
                          }
                          placeholder={f.placeholder || undefined}
                          required={fieldRequired}
                          disabled={readOnly}
                          error={!!fieldIssue}
                          helperText={helper || undefined}
                          sx={baseSx}
                        />
                      );
                    })}
                </div>
              )}

              {fieldDefs.length > 0 && structuredBranchInactive && !hasExistingNotes && (
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.7, fontStyle: 'italic', mb: 1 }}
                >
                  {inactiveStructuredMessage}
                </Typography>
              )}

              {/* Template-driven generic evidence controls.
                  Existing NVD structured fields above remain untouched and may
                  coexist with the step-level evidence control (for example its
                  completion checkbox or additional notes). */}
              {evidenceType === 'text' && showSupplementalNotes && (
                <TextField
                  label={fieldDefs.length ? 'Additional notes (optional)' : 'Notes'}
                  value={a.valueText || ''}
                  onChange={(e) =>
                    updAns(s.order, {
                      valueText: e.target.value,
                    })
                  }
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder={placeholder || undefined}
                  helperText={helperText || undefined}
                  required={stepRequired && fieldDefs.length === 0}
                  disabled={readOnly}
                />
              )}

              {evidenceType === 'textarea' && showSupplementalNotes && (
                <TextField
                  label={fieldDefs.length ? 'Additional notes (optional)' : 'Details'}
                  value={a.valueText || ''}
                  onChange={(e) =>
                    updAns(s.order, { valueText: e.target.value })
                  }
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder={placeholder || undefined}
                  helperText={helperText || undefined}
                  required={stepRequired && fieldDefs.length === 0}
                  disabled={readOnly}
                />
              )}

              {evidenceType === 'count' && (
                <TextField
                  label={sanitizeDisplayText(s.label || 'Count')}
                  type="number"
                  value={
                    typeof a.valueCount === 'number'
                      ? a.valueCount
                      : (fixedNumericValue ?? '')
                  }
                  onChange={(e) =>
                    updAns(s.order, { valueCount: Number(e.target.value || 0) })
                  }
                  inputProps={numericInputProps(s)}
                  InputProps={
                    fixedNumericValue !== undefined
                      ? { readOnly: true }
                      : undefined
                  }
                  helperText={
                    fixedNumericValue !== undefined
                      ? `Fixed by this activity template: ${fixedNumericValue}`
                      : (helperText || undefined)
                  }
                  required={stepRequired}
                  disabled={readOnly}
                />
              )}

              {evidenceType === 'number' && (
                <TextField
                  label={sanitizeDisplayText(s.label || s.title || 'Number')}
                  type="number"
                  value={
                    typeof a.valueCount === 'number'
                      ? a.valueCount
                      : (fixedNumericValue ?? '')
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    updAns(s.order, {
                      valueCount: raw === '' ? undefined : Number(raw),
                    });
                  }}
                  inputProps={numericInputProps(s)}
                  InputProps={
                    fixedNumericValue !== undefined
                      ? { readOnly: true }
                      : undefined
                  }
                  placeholder={placeholder || undefined}
                  helperText={
                    fixedNumericValue !== undefined
                      ? `Fixed by this activity template: ${fixedNumericValue}`
                      : (helperText || undefined)
                  }
                  required={stepRequired}
                  disabled={readOnly}
                />
              )}

              {evidenceType === 'checkbox' && (
                <div className="mt-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!a.valueBool}
                      onChange={(e) =>
                        updAns(s.order, { valueBool: !!e.target.checked })
                      }
                      disabled={readOnly}
                    />
                    <span>{sanitizeDisplayText(s.label || 'Completed')}</span>
                  </label>
                  {helperText && (
                    <Typography variant="caption" sx={{ opacity: 0.72, display: 'block', mt: 0.5 }}>
                      {helperText}
                    </Typography>
                  )}
                </div>
              )}

              {evidenceType === 'boolean' && (
                <TextField
                  select
                  label={sanitizeDisplayText(s.label || s.title || 'Response')}
                  value={typeof a.valueBool === 'boolean' ? String(a.valueBool) : ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    updAns(s.order, {
                      valueBool: raw === '' ? undefined : raw === 'true',
                    });
                  }}
                  fullWidth
                  helperText={helperText || undefined}
                  required={stepRequired}
                  disabled={readOnly}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              )}

              {evidenceType === 'date' && (
                <TextField
                  label={sanitizeDisplayText(s.label || s.title || 'Date')}
                  type="date"
                  value={a.valueText || ''}
                  onChange={(e) =>
                    updAns(s.order, { valueText: e.target.value })
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText={helperText || undefined}
                  required={stepRequired}
                  disabled={readOnly}
                />
              )}

              {evidenceType === 'select' && stepOptions.length > 0 && (
                <TextField
                  select
                  label={sanitizeDisplayText(s.label || s.title || 'Select')}
                  value={a.valueText || ''}
                  onChange={(e) =>
                    updAns(s.order, { valueText: e.target.value })
                  }
                  fullWidth
                  helperText={helperText || undefined}
                  required={stepRequired}
                  disabled={readOnly}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {stepOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {evidenceType === 'select' && stepOptions.length === 0 && (
                <TextField
                  label={sanitizeDisplayText(s.label || s.title || 'Response')}
                  value={a.valueText || ''}
                  onChange={(e) =>
                    updAns(s.order, { valueText: e.target.value })
                  }
                  fullWidth
                  placeholder={placeholder || undefined}
                  helperText={stepHelperText(
                    s,
                    'No predefined options are configured for this template; enter the response manually.'
                  )}
                  required={stepRequired}
                  disabled={readOnly}
                />
              )}

              {evidenceType === 'multiselect' && stepOptions.length > 0 && (
                <TextField
                  select
                  label={sanitizeDisplayText(s.label || s.title || 'Select one or more')}
                  value={parseMultiselectValue(a.valueText)}
                  onChange={(e) => {
                    const selected = Array.isArray(e.target.value)
                      ? e.target.value
                      : String(e.target.value || '').split(',');
                    updAns(s.order, {
                      valueText: encodeMultiselectValue(selected),
                    });
                  }}
                  fullWidth
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => {
                      const selectedValues = Array.isArray(selected) ? selected : [];
                      const labels = selectedValues.map((value) => {
                        const option = stepOptions.find((item) => item.value === String(value));
                        return option?.label || String(value);
                      });
                      return labels.join(', ');
                    },
                  }}
                  helperText={helperText || undefined}
                  required={stepRequired}
                  disabled={readOnly}
                >
                  {stepOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {evidenceType === 'multiselect' && stepOptions.length === 0 && (
                <TextField
                  label={sanitizeDisplayText(s.label || s.title || 'Responses')}
                  value={parseMultiselectValue(a.valueText).join(', ')}
                  onChange={(e) =>
                    updAns(s.order, {
                      valueText: encodeMultiselectValue(
                        String(e.target.value || '')
                          .split(/[|,\n]/g)
                          .map((value) => value.trim())
                          .filter(Boolean)
                      ),
                    })
                  }
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder={placeholder || 'Enter multiple values separated by commas'}
                  helperText={stepHelperText(
                    s,
                    'No predefined options are configured for this template; enter multiple values separated by commas.'
                  )}
                  required={stepRequired}
                  disabled={readOnly}
                />
              )}

              {evidenceType === 'file' && (
                <TextField
                  label={sanitizeDisplayText(s.label || 'File reference / URL')}
                  value={a.valueText || ''}
                  onChange={(e) =>
                    updAns(s.order, { valueText: e.target.value })
                  }
                  fullWidth
                  placeholder={placeholder || undefined}
                  helperText={stepHelperText(
                    s,
                    'Paste the uploaded file URL or stored file reference. Existing upload wiring is unchanged.'
                  )}
                  required={stepRequired}
                  disabled={readOnly}
                />
              )}

              {evidenceType === 'photo' && (
                <TextField
                  label={sanitizeDisplayText(s.label || 'Photo URL (optional)')}
                  value={(a.photos || [])[0] || ''}
                  onChange={(e) =>
                    updAns(s.order, {
                      photos: e.target.value ? [e.target.value] : [],
                    })
                  }
                  fullWidth
                  placeholder={placeholder || undefined}
                  disabled={readOnly}
                  helperText={stepHelperText(
                    s,
                    'Paste an uploaded image URL (existing uploader wiring is unchanged).'
                  )}
                  required={stepRequired}
                />
              )}

              {stepIssue && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
                  {stepIssue.reason}
                </Typography>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions – Submit (Intern: to reviewer; non-Intern: direct finalization). Silently saves before submit. */}
      <div className="flex flex-wrap gap-2 justify-end mt-4">
        <Tooltip
          title={
            readOnly
              ? 'Already submitted/approved'
              : (isIntern ? 'Save & send this run for review' : 'Save & finalize this run')
          }
        >
          <span>
            <Button
              variant="contained"
              onClick={submit}
              disabled={readOnly || submitting}
              startIcon={<LaunchIcon />}
            >
              {submitting ? 'Submitting…' : (isIntern ? 'Submit for Review' : 'Submit')}
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
}